import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { CircularProgress, Switch, TextField as MuiTextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

/* ─── Design tokens ─────────────────────────────────────── */
const T = {
  bg: '#f7f8fa', panel: '#ffffff', card: '#f9fafb', cardHover: '#f3f4f6',
  input: '#f4f5f7', border: '#e5e7eb', borderActive: '#d1d5db',
  textPrimary: '#1a1a2e', textSecondary: '#6b7280', textDim: '#9ca3af', textBright: '#0f172a',
  accent: '#3b82f6', accentDim: 'rgba(59,130,246,0.06)', accentBorder: 'rgba(59,130,246,0.3)',
  success: '#10b981', successDim: 'rgba(16,185,129,0.08)',
  warn: '#f59e0b', warnDim: 'rgba(245,158,11,0.08)',
  danger: '#ef4444', dangerDim: 'rgba(239,68,68,0.08)',
  radius: 8, radiusLg: 12, radiusXl: 16,
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  shadowLg: '0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
  shadowXl: '0 12px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace"
}

const CT = {
  labor:       { label: 'LABOR',       color: '#2563eb', bg: 'rgba(37,99,235,0.07)',  border: 'rgba(37,99,235,0.2)',  dot: '#2563eb' },
  equipment:   { label: 'EQUIPMENT',   color: '#d97706', bg: 'rgba(217,119,6,0.07)',  border: 'rgba(217,119,6,0.2)',  dot: '#d97706' },
  material:    { label: 'MATERIAL',    color: '#059669', bg: 'rgba(5,150,105,0.07)',   border: 'rgba(5,150,105,0.2)',  dot: '#059669' },
  subcontract: { label: 'SUBCONTRACT', color: '#7c3aed', bg: 'rgba(124,58,237,0.07)', border: 'rgba(124,58,237,0.2)', dot: '#7c3aed' },
  disposal:    { label: 'DISPOSAL',    color: '#dc2626', bg: 'rgba(220,38,38,0.07)',   border: 'rgba(220,38,38,0.2)',  dot: '#dc2626' }
}

const CAT_COLORS = {
  'Chemical Applications': '#8b5cf6', 'Herbicide & Chemical': '#8b5cf6',
  'Demo': '#ef4444', 'Earthwork': '#d97706', 'Irrigation': '#3b82f6',
  'Planting': '#10b981', 'Turf': '#84cc16', 'Hardscape': '#6366f1',
  'Lighting': '#f59e0b', 'Drainage': '#06b6d4', 'General': '#6b7280',
  'Water Feature': '#0ea5e9', 'Erosion Control': '#a3e635'
}

/* ─── Micro components ──────────────────────────────────── */
const Badge = ({ type }) => {
  const ct = CT[type] || CT.labor
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
      borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      fontFamily: T.mono, color: ct.color, backgroundColor: ct.bg, border: `1px solid ${ct.border}`
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: ct.dot }} />
      {ct.label}
    </span>
  )
}

const Tag = ({ type, children, onRemove }) => {
  const c = { include: T.success, exclude: T.danger, modify: T.warn, add: T.accent }[type] || T.textDim
  const [hovered, setHovered] = useState(false)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontFamily: T.mono, fontWeight: 600,
      padding: '3px 8px', borderRadius: 5, marginRight: 4, marginTop: 2, position: 'relative',
      color: c, backgroundColor: `${c}14`, letterSpacing: '0.02em', transition: 'all 0.15s'
    }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {children}
      {onRemove && hovered && (
        <span onClick={e => { e.stopPropagation(); onRemove() }}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 14, height: 14, borderRadius: '50%', background: 'rgba(0,0,0,0.08)',
            color: 'currentColor', fontSize: 9, lineHeight: '14px', cursor: 'pointer', marginLeft: 2
          }}>x</span>
      )}
    </span>
  )
}

const CatChip = ({ category }) => {
  const color = CAT_COLORS[category] || '#6b7280'
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 8px',
      borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.06em',
      color, backgroundColor: `${color}14`
    }}>{category}</span>
  )
}

const COST_TYPES = ['labor', 'equipment', 'material', 'subcontract', 'disposal']

const CostTypeSelector = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const ct = CT[value] || CT.labor
  return (
    <div style={{ position: 'relative' }}>
      <span onClick={() => setOpen(!open)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
        borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
        fontFamily: T.mono, color: ct.color, backgroundColor: ct.bg,
        border: `1px solid ${ct.border}`, cursor: 'pointer', transition: 'all 0.15s',
        userSelect: 'none'
      }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: ct.dot }} />
        {ct.label}
        <span style={{ fontSize: 8, marginLeft: 2, opacity: 0.5 }}>▾</span>
      </span>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
          background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)', padding: 4, minWidth: 130,
          animation: 'fadeIn 0.1s ease'
        }}>
          {COST_TYPES.map(type => {
            const c = CT[type]
            const isCurrent = type === value
            return (
              <div key={type} onClick={() => { onChange(type); setOpen(false) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  borderRadius: 4, cursor: 'pointer', transition: 'background 0.1s',
                  background: isCurrent ? c.bg : 'transparent',
                  fontSize: 11, fontWeight: 600, fontFamily: T.mono, color: c.color,
                  letterSpacing: '0.04em'
                }}
                onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = T.cardHover }}
                onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = isCurrent ? c.bg : 'transparent' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.dot }} />
                {c.label}
                {isCurrent && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
              </div>
            )
          })}
        </div>
      )}
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 19 }} onClick={() => setOpen(false)} />}
    </div>
  )
}

const FlagToggle = ({ label, icon, color, dimColor, active, onClick, laborOnly, costType }) => {
  if (laborOnly && costType !== 'labor') return null
  const col = active ? color : (dimColor || T.textDim)
  return (
    <span onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 9, fontFamily: T.mono, fontWeight: 700, padding: '3px 7px',
      borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
      cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
      color: col, backgroundColor: active ? `${color}12` : 'transparent',
      border: active ? `1px solid ${color}25` : '1px solid transparent',
      opacity: active ? 1 : 0.3
    }}>
      <span style={{ fontSize: 10 }}>{icon}</span> {label}
    </span>
  )
}

/* ─── Variation engine ──────────────────────────────────── */
function evalCond(when, val) {
  if (!when) return false
  switch (when.operator) {
    case 'eq': return val === when.value
    case 'gt': return Number(val) > Number(when.value)
    case 'gte': return Number(val) >= Number(when.value)
    case 'lt': return Number(val) < Number(when.value)
    case 'lte': return Number(val) <= Number(when.value)
    default: return false
  }
}

function applyVariations(components, variations, selections) {
  let result = components.map((c, i) => ({ ...c, _i: i, _excluded: false, _mult: 1, _added: false }))
  const added = [], effects = []
  for (const v of (variations || [])) {
    const val = selections[v.attributeId]
    if (val === undefined || val === null) continue
    for (const rule of (v.rules || [])) {
      if (!evalCond(rule.when, val)) continue
      for (const eff of (rule.effects || [])) {
        const tgt = result.find(c => c.description === (eff.targetDescription || '') || c._baseId === (eff.targetId || ''))
        if (eff.action === 'exclude_component' && tgt) { tgt._excluded = true; effects.push({ type: 'exclude', target: tgt.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'include_component' && tgt) { tgt._excluded = false; tgt.isOptional = false; effects.push({ type: 'include', target: tgt.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'multiply_qty' && tgt) { tgt._mult *= (eff.value || 1); effects.push({ type: 'modify', target: tgt.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'add_component' && eff.value) { added.push({ ...eff.value, _excluded: false, _mult: 1, _added: true, _by: v.label }); effects.push({ type: 'add', target: eff.value.description, note: eff.note, variation: v.label }) }
      }
    }
  }
  return { components: [...result, ...added], effects }
}

/* ─── Client-side formula evaluator (mirrors server) ─── */
function clientEvalFormula(str, vars) {
  if (!str) return 0
  try {
    const tokens = []
    let i = 0
    while (i < str.length) {
      const ch = str[i]
      if (/\s/.test(ch)) { i++; continue }
      if ('+-*/()'.includes(ch)) { tokens.push({ t: ch === '(' ? 'L' : ch === ')' ? 'R' : 'O', v: ch }); i++; continue }
      if (/[0-9.]/.test(ch)) { let n = ''; while (i < str.length && /[0-9.]/.test(str[i])) n += str[i++]; tokens.push({ t: 'N', v: parseFloat(n) || 0 }); continue }
      if (/[a-zA-Z_]/.test(ch)) { let nm = ''; while (i < str.length && /[a-zA-Z0-9_]/.test(str[i])) nm += str[i++]; tokens.push({ t: 'V', v: nm }); continue }
      i++
    }
    let pos = 0
    const peek = () => pos < tokens.length ? tokens[pos] : null
    const eat = () => tokens[pos++]
    function parseExpr() {
      let l = parseTerm()
      while (peek() && peek().t === 'O' && (peek().v === '+' || peek().v === '-')) { const op = eat().v; const r = parseTerm(); l = op === '+' ? l + r : l - r }
      return l
    }
    function parseTerm() {
      let l = parseFact()
      while (peek() && peek().t === 'O' && (peek().v === '*' || peek().v === '/')) { const op = eat().v; const r = parseFact(); l = op === '/' ? (r === 0 ? 0 : l / r) : l * r }
      return l
    }
    function parseFact() {
      const tk = peek()
      if (!tk) return 0
      if (tk.t === 'O' && tk.v === '-') { eat(); return -parseFact() }
      if (tk.t === 'L') { eat(); const v = parseExpr(); if (peek() && peek().t === 'R') eat(); return v }
      if (tk.t === 'N') { eat(); return tk.v }
      if (tk.t === 'V') { eat(); const val = vars[tk.v]; return typeof val === 'number' ? val : 0 }
      eat(); return 0
    }
    const result = parseExpr()
    return isFinite(result) ? result : 0
  } catch { return 0 }
}

/* ─── Effect action metadata ──────────────────────────── */
const EFFECT_ACTIONS = [
  { key: 'exclude_component', label: 'Exclude Component', desc: 'Remove a component from the recipe', icon: '\uD83D\uDEAB', tagType: 'exclude', tagColor: T.danger },
  { key: 'include_component', label: 'Include Component', desc: 'Force-include an optional component', icon: '\u2705', tagType: 'include', tagColor: T.success },
  { key: 'multiply_qty', label: 'Multiply Quantity', desc: "Scale a component's hours or qty", icon: '\u2716\uFE0F', tagType: 'modify', tagColor: T.warn },
  { key: 'add_component', label: 'Add Component', desc: 'Add a new component to the recipe', icon: '\u2795', tagType: 'add', tagColor: T.accent }
]

/* ═══════════════════════════════════════════════════════════
   CONFIRM MODAL
   ═══════════════════════════════════════════════════════════ */
const ConfirmModal = ({ modal, onClose }) => {
  const [closing, setClosing] = useState(false)
  if (!modal) return null

  const doClose = () => {
    setClosing(true)
    setTimeout(() => { setClosing(false); onClose() }, 180)
  }
  const doConfirm = () => {
    if (modal.onConfirm) modal.onConfirm()
    setClosing(true)
    setTimeout(() => { setClosing(false); onClose() }, 180)
  }

  return (
    <div onClick={e => { if (e.target === e.currentTarget) doClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15,23,42,0.35)', backdropFilter: 'blur(6px)',
        animation: closing ? 'cfmFadeOut 0.2s ease forwards' : 'cfmFadeIn 0.2s ease'
      }}>
      <div style={{
        background: T.panel, borderRadius: T.radiusXl, width: 420, overflow: 'hidden',
        boxShadow: T.shadowXl,
        animation: closing ? 'cfmBoxOut 0.18s ease forwards' : 'cfmBoxIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}>
        <div style={{ padding: '24px 28px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0, background: T.dangerDim
          }}>{'\u26A0\uFE0F'}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.textBright, margin: 0 }}>{modal.title}</div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>{modal.subtitle || 'This action cannot be undone'}</div>
          </div>
        </div>
        <div style={{ padding: '0 28px 20px' }}>
          <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6 }}>{modal.message}</div>
        </div>
        <div style={{ padding: '16px 28px', background: T.card, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={doClose} style={{
            padding: '8px 18px', borderRadius: T.radius, fontSize: 13, fontWeight: 600, fontFamily: T.font,
            cursor: 'pointer', background: 'transparent', border: `1px solid ${T.border}`, color: T.textSecondary,
            transition: 'all 0.15s'
          }}>Cancel</button>
          <button onClick={doConfirm} style={{
            padding: '8px 18px', borderRadius: T.radius, fontSize: 13, fontWeight: 600, fontFamily: T.font,
            cursor: 'pointer', background: T.danger, color: '#fff', border: 'none',
            boxShadow: '0 1px 3px rgba(239,68,68,0.3)', transition: 'all 0.15s'
          }}>{modal.actionLabel || 'Remove'}</button>
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   EFFECT BUILDER MODAL
   ═══════════════════════════════════════════════════════════ */
const EffectBuilderModal = ({ modal, comps, onClose, onSave }) => {
  const [action, setAction] = useState(null)
  const [target, setTarget] = useState(null)
  const [mult, setMult] = useState(1.5)
  const [addCostType, setAddCostType] = useState('labor')
  const [addDesc, setAddDesc] = useState('')
  const [addRole, setAddRole] = useState('')
  const [note, setNote] = useState('')

  if (!modal) return null

  const isValid = () => {
    if (!action) return false
    if (action === 'exclude_component' || action === 'include_component') return !!target
    if (action === 'multiply_qty') return !!target
    if (action === 'add_component') return !!addDesc
    return false
  }

  const handleSave = () => {
    const effect = { action, note }
    if (action === 'exclude_component' || action === 'include_component') {
      effect.targetDescription = target
    } else if (action === 'multiply_qty') {
      effect.targetDescription = target
      effect.value = mult
    } else if (action === 'add_component') {
      effect.value = {
        costType: addCostType, description: addDesc,
        laborRole: addCostType === 'labor' ? (addRole || 'General Labor') : '',
        isOptional: false, followsLeadHours: addCostType === 'labor',
        minimumHours: addCostType === 'labor' ? 2 : addCostType === 'equipment' ? 4 : null
      }
    }
    onSave(effect, modal.variationIdx, modal.optionValue)
  }

  const previewTag = () => {
    if (!action) return null
    if (action === 'exclude_component' && target) return { type: 'exclude', text: `Exclude: ${target}` }
    if (action === 'include_component' && target) return { type: 'include', text: `Include: ${target}` }
    if (action === 'multiply_qty' && target) return { type: 'modify', text: `${target} x${mult}` }
    if (action === 'add_component' && addDesc) return { type: 'add', text: `+ ${addDesc}` }
    return null
  }

  const pv = previewTag()
  const pvColors = { exclude: T.danger, include: T.success, modify: T.warn, add: T.accent }

  // Compute step number for note field
  const noteStep = action === 'multiply_qty' && target ? 4 : (action === 'add_component' ? 3 : (action && target ? 3 : 3))

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.2)', backdropFilter: 'blur(4px)',
        zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn 0.15s ease'
      }}>
      <div style={{
        background: T.panel, borderRadius: T.radiusXl, width: 580, maxHeight: '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: T.shadowXl,
        animation: 'slideUp 0.25s ease'
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 14px', borderBottom: `1px solid ${T.border}`, background: T.card }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: T.textBright }}>Add Effect</span>
            <button onClick={onClose} style={{
              border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 20,
              padding: '2px 6px', borderRadius: 4, transition: 'all 0.15s', lineHeight: 1
            }}>&times;</button>
          </div>
          <div style={{ fontSize: 12, color: T.textSecondary }}>
            When <strong style={{ color: T.textPrimary, fontWeight: 600 }}>{modal.optionLabel}</strong> is selected on <strong style={{ color: T.textPrimary, fontWeight: 600 }}>{modal.variationLabel}</strong>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 24px' }}>
          {/* Step 1: Action cards */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.accent, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>What should happen?</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {EFFECT_ACTIONS.map(a => {
                const sel = action === a.key
                const borderCol = sel ? a.tagColor : T.border
                const bgCol = sel ? `${a.tagColor}10` : T.panel
                return (
                  <div key={a.key} onClick={() => { setAction(a.key); setTarget(null) }}
                    style={{
                      padding: '14px 16px', border: `1.5px solid ${borderCol}`, borderRadius: T.radiusLg,
                      cursor: 'pointer', transition: 'all 0.2s', background: bgCol, position: 'relative', overflow: 'hidden'
                    }}>
                    {sel && (
                      <span style={{
                        position: 'absolute', top: 8, right: 10, width: 20, height: 20, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff',
                        background: a.tagColor
                      }}>{'\u2713'}</span>
                    )}
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{a.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.textBright, marginBottom: 2 }}>{a.label}</div>
                    <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.4 }}>{a.desc}</div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Step 2: Target picker (for exclude/include/multiply) */}
          {action && action !== 'add_component' && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.accent, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>2</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>
                  {action === 'exclude_component' ? 'Which component to exclude?' :
                   action === 'include_component' ? 'Which component to include?' : 'Which component to scale?'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {comps.map((c, ci) => {
                  const sel = target === c.description
                  return (
                    <div key={ci} onClick={() => setTarget(c.description)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                        border: sel ? `1.5px solid ${T.accent}` : `1.5px solid ${T.border}`,
                        borderRadius: T.radius, cursor: 'pointer', transition: 'all 0.15s',
                        background: sel ? T.accentDim : T.panel
                      }}>
                      <Badge type={c.costType} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: T.textPrimary, flex: 1 }}>{c.description}</span>
                      <span style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>{c.laborRole || '\u2014'}</span>
                      <span style={{
                        width: 18, height: 18, borderRadius: '50%', border: sel ? 'none' : `2px solid ${T.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        fontSize: 10, background: sel ? T.accent : 'transparent', color: sel ? '#fff' : 'transparent',
                        transition: 'all 0.15s'
                      }}>{sel ? '\u2713' : ''}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 2b: Multiplier input (multiply only) */}
          {action === 'multiply_qty' && target && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.accent, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>3</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>By how much?</span>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 16,
                background: T.card, borderRadius: T.radiusLg, border: `1px solid ${T.border}`
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Multiplier</span>
                <input type="number" value={mult} step="0.1" min="0.1"
                  onChange={e => setMult(parseFloat(e.target.value) || 1)}
                  style={{
                    width: 80, padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                    fontSize: 16, fontFamily: T.mono, fontWeight: 600, color: T.warn, textAlign: 'center',
                    outline: 'none', background: T.panel, transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = T.warn}
                  onBlur={e => e.target.style.borderColor = T.border} />
                <span style={{ fontSize: 11, color: T.textDim }}>x original quantity</span>
                <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                  {[1.2, 1.5, 2.0, 3.0].map(v => (
                    <span key={v} onClick={() => setMult(v)}
                      style={{
                        padding: '4px 10px', border: `1px solid ${mult === v ? T.warn : T.border}`, borderRadius: 20,
                        fontSize: 11, fontFamily: T.mono, fontWeight: 600, color: mult === v ? T.warn : T.textSecondary,
                        cursor: 'pointer', background: mult === v ? T.warnDim : T.panel, transition: 'all 0.15s'
                      }}>{v}x</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2c: Add component form */}
          {action === 'add_component' && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.accent, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>2</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>New component details</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 6 }}>Cost Type</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['labor', 'equipment', 'material', 'subcontract', 'disposal'].map(ct => {
                      const c = CT[ct]
                      const sel = addCostType === ct
                      return (
                        <span key={ct} onClick={() => setAddCostType(ct)}
                          style={{
                            padding: '6px 12px', border: `1.5px solid ${sel ? c.border : T.border}`, borderRadius: 20,
                            fontSize: 11, fontFamily: T.mono, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                            textTransform: 'uppercase', letterSpacing: '0.04em', color: c.color,
                            background: sel ? c.bg : 'transparent'
                          }}>{ct}</span>
                      )
                    })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim }}>Description</label>
                    <input type="text" value={addDesc} onChange={e => setAddDesc(e.target.value)} placeholder="e.g. Backpack Sprayer"
                      style={{
                        padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                        fontSize: 13, fontFamily: T.font, color: T.textPrimary, background: T.panel, outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = T.accent}
                      onBlur={e => e.target.style.borderColor = T.border} />
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim }}>Role (if labor)</label>
                    <input type="text" value={addRole} onChange={e => setAddRole(e.target.value)} placeholder="e.g. Helper"
                      style={{
                        padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                        fontSize: 13, fontFamily: T.font, color: T.textPrimary, background: T.panel, outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = T.accent}
                      onBlur={e => e.target.style.borderColor = T.border} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step N: Note */}
          {action && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.accent, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{noteStep}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Describe what this effect does</span>
              </div>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder="e.g. 'No vehicle access \u2014 use backpack sprayer instead'"
                style={{
                  width: '100%', padding: '10px 14px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                  fontSize: 13, fontFamily: T.font, color: T.textPrimary, background: T.panel, outline: 'none',
                  resize: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
                }}
                onFocus={e => e.target.style.borderColor = T.accent}
                onBlur={e => e.target.style.borderColor = T.border} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', background: T.card
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textSecondary }}>
            {pv && (
              <>
                <span style={{ fontSize: 11, color: T.textDim }}>Preview:</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontFamily: T.mono,
                  fontWeight: 600, padding: '3px 8px', borderRadius: 5,
                  color: pvColors[pv.type], backgroundColor: `${pvColors[pv.type]}12`
                }}>{pv.text}</span>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '8px 18px', borderRadius: T.radius, fontSize: 13, fontWeight: 600, fontFamily: T.font,
              cursor: 'pointer', background: 'transparent', border: `1px solid ${T.border}`, color: T.textSecondary,
              transition: 'all 0.15s'
            }}>Cancel</button>
            <button onClick={handleSave} disabled={!isValid()} style={{
              padding: '8px 18px', borderRadius: T.radius, fontSize: 13, fontWeight: 600, fontFamily: T.font,
              cursor: isValid() ? 'pointer' : 'not-allowed', background: isValid() ? T.accent : T.border,
              color: '#fff', border: 'none', boxShadow: isValid() ? '0 1px 3px rgba(59,130,246,0.3)' : 'none',
              transition: 'all 0.15s', opacity: isValid() ? 1 : 0.5
            }}>Add Effect</button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
const ServiceRecipeBuilder = ({ url }) => {
  const [services, setServices] = useState([])
  const [templates, setTemplates] = useState([])
  const [laborRates, setLaborRates] = useState([])
  const [equipmentRates, setEquipmentRates] = useState([])
  const [productionRates, setProductionRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [comps, setComps] = useState([])
  const [vars, setVars] = useState([])
  const [inputSlots, setInputSlots] = useState([])
  const [slotValues, setSlotValues] = useState({})
  const [dirty, setDirty] = useState(false)
  const [minimumTotal, setMinimumTotal] = useState(null)
  const [pvSel, setPvSel] = useState({})
  const [catFilter, setCatFilter] = useState('All')
  const [section, setSection] = useState('components')
  const [defaultMaterials, setDefaultMaterials] = useState([])
  const [childRecipes, setChildRecipes] = useState([])
  const [classBreakdowns, setClassBreakdowns] = useState({})

  // New state for full-width table + fullscreen editor
  const [editIdx, setEditIdx] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [confirmModal, setConfirmModal] = useState(null)
  const [effectModal, setEffectModal] = useState(null)

  const fetchAll = useCallback(() => {
    setLoading(true)
    Promise.all([
      axios.get(`${url}/api/bid-estimation/services?includeInactive=true`),
      axios.get(`${url}/api/bid-estimation/service-templates`),
      axios.get(`${url}/api/bid-estimation/labor-rates`),
      axios.get(`${url}/api/bid-estimation/equipment-rates`),
      axios.get(`${url}/api/bid-estimation/production-rates`)
    ]).then(([s, t, l, e, p]) => {
      setServices(s.data || []); setTemplates(t.data || [])
      setLaborRates(l.data || []); setEquipmentRates(e.data || [])
      setProductionRates(p.data || []); setLoading(false)
    }).catch(() => setLoading(false))
  }, [url])

  useEffect(() => { fetchAll() }, [fetchAll])

  const tMap = useMemo(() => {
    const m = {}; for (const t of templates) m[t.serviceId] = t; return m
  }, [templates])

  const allWithRecipes = useMemo(() => services.filter(s => !s.entity ? !!tMap[s.serviceId] : true), [services, tMap])
  const categories = useMemo(() => ['All', ...new Set(allWithRecipes.map(s => s.category))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)), [allWithRecipes])

  const svcList = useMemo(() => {
    let list = allWithRecipes
    if (catFilter !== 'All') list = list.filter(s => s.category === catFilter)
    if (search) { const q = search.toLowerCase(); list = list.filter(s => s.name.toLowerCase().includes(q) || s.category.toLowerCase().includes(q) || (s.serviceId && s.serviceId.toLowerCase().includes(q))) }
    return list
  }, [allWithRecipes, catFilter, search])

  const svc = editIdx !== null ? svcList[editIdx] : null
  const tmpl = svc ? tMap[svc.serviceId] : null

  // Load template data when opening editor
  useEffect(() => {
    if (editIdx !== null && tmpl) {
      setComps(JSON.parse(JSON.stringify(tmpl.components || [])))
      setVars(JSON.parse(JSON.stringify(tmpl.variations || [])))
      setInputSlots(JSON.parse(JSON.stringify(tmpl.inputSlots || [])))
      setDefaultMaterials(JSON.parse(JSON.stringify(tmpl.defaultMaterials || [])))
      setChildRecipes(JSON.parse(JSON.stringify(tmpl.childRecipes || [])))
      setMinimumTotal(tmpl.minimumTotal || null)
      const s = {}; for (const v of (tmpl.variations || [])) s[v.attributeId] = v.defaultValue; setPvSel(s)
      const sv = {}; for (const slot of (tmpl.inputSlots || [])) { if (slot.defaultValue != null) sv[slot.key] = slot.defaultValue }; setSlotValues(sv)
      setClassBreakdowns({})
    } else if (editIdx !== null) {
      setComps([]); setVars([]); setInputSlots([]); setDefaultMaterials([]); setChildRecipes([]); setPvSel({}); setSlotValues({}); setMinimumTotal(null); setClassBreakdowns({})
    }
    setDirty(false)
  }, [tmpl?._id, editIdx])

  const openEditor = (i) => {
    if (dirty) {
      setConfirmModal({
        title: 'Unsaved Changes',
        subtitle: 'You have unsaved work',
        message: 'Switching services will discard your unsaved changes to this recipe.',
        actionLabel: 'Discard & Switch',
        onConfirm: () => { setEditIdx(i); setSection('components'); setDirty(false) }
      })
      return
    }
    setEditIdx(i); setSection('components')
  }

  const closeEditor = () => {
    if (dirty) {
      setConfirmModal({
        title: 'Unsaved Changes',
        subtitle: 'You have unsaved work',
        message: 'Closing the editor will discard your unsaved changes to this recipe.',
        actionLabel: 'Discard & Close',
        onConfirm: () => { setEditIdx(null); setDirty(false) }
      })
      return
    }
    setEditIdx(null)
  }

  const toggleExpand = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))

  const save = async () => {
    if (!svc) return; setSaving(true)
    try {
      await axios.post(`${url}/api/bid-estimation/service-templates`, {
        serviceId: svc.serviceId, serviceName: svc.name, serviceCategory: svc.category,
        components: comps.filter(c => c.description || c.costType), variations: vars,
        inputSlots: inputSlots.filter(s => s.key && s.label),
        defaultMaterials: defaultMaterials.filter(m => m.label || m.inputSlotKey || m.formula),
        childRecipes: childRecipes.filter(cr => cr.childServiceId),
        minimumTotal: minimumTotal || undefined
      })
      setSaving(false); setDirty(false); fetchAll()
    } catch (e) { setSaving(false); console.error(e) }
  }

  const preview = useMemo(() => applyVariations(comps, vars, pvSel), [comps, vars, pvSel])

  // Effect builder handlers
  const openEffectBuilder = (variationIdx, optionValue) => {
    const v = vars[variationIdx]
    let optionLabel = optionValue
    if (v.type === 'select') {
      const o = (v.options || []).find(x => x.value === optionValue)
      if (o) optionLabel = o.label || o.value
    } else if (v.type === 'boolean') {
      optionLabel = optionValue === true ? 'Enabled' : 'Disabled'
    }
    setEffectModal({ variationIdx, optionValue, optionLabel, variationLabel: v.label || v.attributeId })
  }

  const handleEffectSave = (effect, variationIdx, optionValue) => {
    const newVars = JSON.parse(JSON.stringify(vars))
    const v = newVars[variationIdx]
    if (!v.rules) v.rules = []
    let rule = v.rules.find(r => r.when?.operator === 'eq' && r.when?.value === optionValue)
    if (!rule) {
      rule = { when: { operator: 'eq', value: optionValue }, effects: [] }
      v.rules.push(rule)
    }
    rule.effects.push(effect)
    setVars(newVars); setDirty(true); setEffectModal(null)
  }

  const removeEffectFromOption = (variationIdx, optionValue, effectGlobalIdx) => {
    const v = vars[variationIdx]
    const effs = (v.rules || []).filter(r => evalCond(r.when, optionValue)).flatMap(r => r.effects || [])
    const e = effs[effectGlobalIdx]
    let desc = ''
    if (e.action === 'exclude_component') desc = `Exclude: ${e.targetDescription}`
    else if (e.action === 'include_component') desc = `Include: ${e.targetDescription}`
    else if (e.action === 'multiply_qty') desc = `${e.targetDescription} x${e.value}`
    else if (e.action === 'add_component') desc = `Add: ${e.value?.description}`

    setConfirmModal({
      title: 'Remove Effect',
      subtitle: 'This action cannot be undone',
      message: `Remove the effect "${desc}" from this variation option?`,
      actionLabel: 'Remove',
      onConfirm: () => {
        const newVars = JSON.parse(JSON.stringify(vars))
        const nv = newVars[variationIdx]
        let count = 0
        for (const rule of (nv.rules || [])) {
          if (evalCond(rule.when, optionValue)) {
            for (let i = 0; i < (rule.effects || []).length; i++) {
              if (count === effectGlobalIdx) {
                rule.effects.splice(i, 1)
                if (!rule.effects.length) nv.rules = nv.rules.filter(r => r !== rule)
                setVars(newVars); setDirty(true)
                return
              }
              count++
            }
          }
        }
      }
    })
  }

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (confirmModal) setConfirmModal(null)
        else if (effectModal) setEffectModal(null)
        else if (editIdx !== null) closeEditor()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [confirmModal, effectModal, editIdx, dirty])

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: T.bg }}><CircularProgress style={{ color: T.accent }} /></div>

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', background: T.bg, fontFamily: T.font }}>
      {/* ─── TOP BAR ─── */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '0 32px', height: 56,
        background: T.panel, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 50
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.5 }}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.textBright }}>Service Recipes</span>
          <span style={{ fontSize: 9, fontFamily: T.mono, fontWeight: 600, color: T.textDim, padding: '2px 7px', border: `1px solid ${T.border}`, borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Builder</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', minWidth: 240 }}>
            <svg style={{ position: 'absolute', left: 12, top: 10, opacity: 0.3 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="text" placeholder="Search services..." value={search} onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 14px 8px 36px', border: `1px solid ${T.border}`, borderRadius: T.radius,
                fontSize: 13, fontFamily: T.font, color: T.textPrimary, background: T.input, outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={e => { e.target.style.borderColor = T.accent; e.target.style.boxShadow = `0 0 0 3px ${T.accentDim}` }}
              onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none' }} />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            style={{
              padding: '8px 14px', border: `1px solid ${T.border}`, borderRadius: T.radius,
              fontSize: 13, fontFamily: T.font, color: T.textPrimary, background: T.input, outline: 'none',
              cursor: 'pointer', minWidth: 180
            }}>
            {categories.map(c => <option key={c} value={c}>{c === 'All' ? `All Categories (${allWithRecipes.length})` : c}</option>)}
          </select>
          <span style={{ fontSize: 12, color: T.textDim, fontFamily: T.mono }}>
            {svcList.length} service{svcList.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ─── TABLE ─── */}
      <div style={{ margin: '20px 32px 40px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, overflow: 'hidden', boxShadow: T.shadow }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                { label: '', width: 28 },
                { label: 'Service' },
                { label: 'Category' },
                { label: 'Components' },
                { label: 'Variations' },
                { label: '', width: 90 }
              ].map((h, i) => (
                <th key={i} style={{
                  textAlign: 'left', padding: '12px 18px', fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim,
                  background: T.card, borderBottom: `1px solid ${T.border}`,
                  ...(h.width ? { width: h.width } : {})
                }}>{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {svcList.map((s, i) => {
              const t = tMap[s.serviceId]
              const compArr = t?.components || []
              const varArr = t?.variations || []
              const childArr = t?.childRecipes || []
              const isExpanded = expanded[i]

              return (
                <React.Fragment key={s._id || i}>
                  <tr onClick={() => toggleExpand(i)}
                    style={{ cursor: 'pointer', transition: 'background 0.1s', borderBottom: `1px solid ${T.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = T.cardHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '14px 18px', width: 28, textAlign: 'center', color: T.textDim, fontSize: 12 }}>
                      {isExpanded ? '\u25BE' : '\u25B8'}
                    </td>
                    <td style={{ padding: '14px 18px' }}>
                      <div style={{ fontWeight: 600, color: T.textBright, fontSize: 13 }}>{s.name}</div>
                      <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textDim, fontWeight: 400, marginTop: 2 }}>{s.serviceId}</div>
                    </td>
                    <td style={{ padding: '14px 18px' }}><CatChip category={s.category} /></td>
                    <td style={{ padding: '14px 18px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {compArr.slice(0, 4).map((c, ci) => <Badge key={ci} type={c.costType} />)}
                        {compArr.length > 4 && <span style={{ fontSize: 10, color: T.textDim, alignSelf: 'center' }}>+{compArr.length - 4}</span>}
                        {childArr.length > 0 && (
                          <span style={{ fontSize: 10, fontFamily: T.mono, fontWeight: 700, color: CT.subcontract.color, background: CT.subcontract.bg, padding: '2px 8px', borderRadius: 10 }}>
                            {childArr.length} child
                          </span>
                        )}
                        {compArr.length === 0 && childArr.length === 0 && <span style={{ fontSize: 12, color: T.textDim, fontStyle: 'italic' }}>{'\u2014'}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '14px 18px' }}>
                      {varArr.length > 0 ? (
                        <span style={{ fontSize: 10, fontFamily: T.mono, fontWeight: 600, color: T.accent, background: T.accentDim, padding: '2px 8px', borderRadius: 10 }}>
                          {varArr.length} var
                        </span>
                      ) : <span style={{ fontSize: 12, color: T.textDim }}>{'\u2014'}</span>}
                    </td>
                    <td style={{ padding: '14px 18px' }}>
                      <button onClick={e => { e.stopPropagation(); openEditor(i) }}
                        style={{
                          padding: '6px 14px', border: `1px solid ${T.border}`, borderRadius: T.radius,
                          fontSize: 12, fontWeight: 600, color: T.textSecondary, background: T.panel,
                          cursor: 'pointer', transition: 'all 0.15s', fontFamily: T.font
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; e.currentTarget.style.background = T.accentDim }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSecondary; e.currentTarget.style.background = T.panel }}>
                        Edit
                      </button>
                    </td>
                  </tr>

                  {/* Expand row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: '0 18px 18px', background: T.card, borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ paddingTop: 16 }}>
                          <div style={{ display: 'flex', gap: 20 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 8 }}>
                                Components ({compArr.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {compArr.map((c, ci) => (
                                  <div key={ci} style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                                    background: T.panel, borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12
                                  }}>
                                    <Badge type={c.costType} />
                                    <span style={{ fontWeight: 500, color: T.textBright }}>{c.description}</span>
                                    {c.isOptional && <FlagToggle label="OPT" icon={'\u25C9'} color={T.warn} active={true} onClick={() => {}} />}
                                    {c.followsLeadHours && <FlagToggle label="FOLLOWS" icon={'\u21E3'} color="#6366f1" active={true} onClick={() => {}} />}
                                    <span style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono, marginLeft: 'auto' }}>{c.laborRole || '\u2014'}</span>
                                  </div>
                                ))}
                                {compArr.length === 0 && <span style={{ color: T.textDim, fontSize: 12, fontStyle: 'italic' }}>No components defined</span>}
                              </div>
                            </div>
                            {/* Input slots summary */}
                            {t?.inputSlots?.length > 0 && (
                              <div style={{ flex: 0.6 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 8 }}>
                                  Inputs ({t.inputSlots.length})
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {t.inputSlots.map((slot, si) => (
                                    <div key={si} style={{
                                      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                                      background: T.panel, borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12
                                    }}>
                                      <span style={{ fontSize: 11, fontFamily: T.mono, fontWeight: 700, color: T.accent, background: T.accentDim, padding: '1px 6px', borderRadius: 3 }}>{slot.unit}</span>
                                      <span style={{ fontWeight: 500, color: T.textBright }}>{slot.label}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 8 }}>
                                Variations ({varArr.length})
                              </div>
                              {varArr.length > 0 ? varArr.map((v, vi) => (
                                <div key={vi} style={{
                                  padding: '8px 12px', background: T.panel, borderRadius: 6,
                                  border: `1px solid ${T.border}`, marginBottom: 6
                                }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textBright, marginBottom: 4 }}>{v.label || v.attributeId}</div>
                                  <div style={{ fontSize: 11, color: T.textDim }}>
                                    {v.type === 'select' ? (v.options || []).map(o => o.label || o.value).join(', ') :
                                     v.type === 'boolean' ? 'Toggle on/off' :
                                     v.type === 'integer' ? `Integer input` :
                                     v.type === 'range' ? `Range: ${v.min || 0} - ${v.max || 100}` : v.type}
                                  </div>
                                </div>
                              )) : <span style={{ color: T.textDim, fontSize: 12, fontStyle: 'italic' }}>No variations defined</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
        {svcList.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: T.textDim }}>
            <div style={{ fontSize: 14 }}>No services match your search</div>
          </div>
        )}
      </div>

      {/* ─── FULLSCREEN EDITOR ─── */}
      {editIdx !== null && svc && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100, background: T.bg,
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          {/* Editor top bar */}
          <div style={{
            display: 'flex', alignItems: 'center', padding: '0 28px', height: 56,
            background: T.panel, borderBottom: `1px solid ${T.border}`, flexShrink: 0
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.textBright, display: 'flex', alignItems: 'center', gap: 10 }}>
                {svc.name}
                <CatChip category={svc.category} />
              </div>
              <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textDim }}>{svc.serviceId}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 20 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recipe Min $</span>
              <input
                type="number" value={minimumTotal || ''} placeholder="—"
                onChange={e => { setMinimumTotal(e.target.value ? parseFloat(e.target.value) : null); setDirty(true) }}
                style={{
                  width: 90, padding: '6px 10px', border: `1.5px solid ${minimumTotal ? T.accentBorder : T.border}`,
                  borderRadius: T.radius, fontFamily: T.mono, fontSize: 14, fontWeight: 600,
                  color: minimumTotal ? T.success : T.textDim, textAlign: 'center', outline: 'none',
                  background: minimumTotal ? `${T.success}08` : T.input, transition: 'all 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = T.accent}
                onBlur={e => e.target.style.borderColor = minimumTotal ? T.accentBorder : T.border}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={closeEditor} style={{
                padding: '8px 18px', borderRadius: T.radius, fontSize: 13, fontWeight: 600, fontFamily: T.font,
                cursor: 'pointer', background: 'transparent', border: `1px solid ${T.border}`, color: T.textSecondary,
                transition: 'all 0.15s'
              }}>Close</button>
              <button onClick={save} disabled={saving || !dirty} style={{
                padding: '8px 18px', borderRadius: T.radius, fontSize: 13, fontWeight: 600, fontFamily: T.font,
                cursor: dirty ? 'pointer' : 'default', background: dirty ? T.accent : T.border, color: '#fff',
                border: 'none', boxShadow: dirty ? '0 1px 3px rgba(59,130,246,0.3)' : 'none',
                transition: 'all 0.15s', opacity: saving ? 0.7 : (dirty ? 1 : 0.5)
              }}>{saving ? 'Saving...' : 'Save Recipe'}</button>
            </div>
          </div>

          {/* Editor tabs */}
          <div style={{
            display: 'flex', gap: 0, padding: '0 28px', background: T.panel,
            borderBottom: `1px solid ${T.border}`, flexShrink: 0
          }}>
            {[['components', 'Components', comps.length], ['inputs', 'Inputs', inputSlots.length], ['materials', 'Materials', defaultMaterials.length], ['variations', 'Variations', vars.length], ['children', 'Child Recipes', childRecipes.length], ['preview', 'Live Preview', null]].map(([key, label, count]) => (
              <button key={key} onClick={() => setSection(key)}
                style={{
                  padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: section === key ? 600 : 500, fontFamily: T.font,
                  color: section === key ? T.accent : T.textSecondary,
                  borderBottom: section === key ? `2px solid ${T.accent}` : '2px solid transparent',
                  marginBottom: -1, transition: 'all 0.15s'
                }}>
                {label}
                {count !== null && count > 0 && (
                  <span style={{ marginLeft: 5, fontSize: 10, fontFamily: T.mono, fontWeight: 700, color: T.accent }}>{count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Editor body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 48px' }}>
            {section === 'components' && (
              <CompTab comps={comps} setComps={c => { setComps(c); setDirty(true) }}
                laborRates={laborRates} equipmentRates={equipmentRates} productionRates={productionRates}
                onConfirm={setConfirmModal} />
            )}
            {section === 'inputs' && (
              <InputSlotsTab slots={inputSlots} setSlots={s => { setInputSlots(s); setDirty(true) }}
                onConfirm={setConfirmModal} productionRates={productionRates} />
            )}
            {section === 'materials' && (
              <MaterialsTab materials={defaultMaterials} setMaterials={m => { setDefaultMaterials(m); setDirty(true) }}
                inputSlots={inputSlots} onConfirm={setConfirmModal} />
            )}
            {section === 'variations' && (
              <VarTab vars={vars} setVars={v => { setVars(v); setDirty(true) }}
                onConfirm={setConfirmModal} onOpenEffectBuilder={openEffectBuilder}
                onRemoveEffect={removeEffectFromOption} />
            )}
            {section === 'children' && (
              <ChildRecipesTab childRecipes={childRecipes} setChildRecipes={cr => { setChildRecipes(cr); setDirty(true) }}
                services={services} inputSlots={inputSlots} onConfirm={setConfirmModal} />
            )}
            {section === 'preview' && <PreviewTab result={preview} vars={vars} sel={pvSel} setSel={setPvSel}
              inputSlots={inputSlots} slotValues={slotValues} setSlotValues={setSlotValues}
              classBreakdowns={classBreakdowns} setClassBreakdowns={setClassBreakdowns}
              defaultMaterials={defaultMaterials} childRecipes={childRecipes}
              laborRates={laborRates} equipmentRates={equipmentRates} productionRates={productionRates}
              minimumTotal={minimumTotal} />}
          </div>
        </div>
      )}

      {/* ─── EFFECT BUILDER MODAL ─── */}
      <EffectBuilderModal modal={effectModal} comps={comps} onClose={() => setEffectModal(null)} onSave={handleEffectSave} />

      {/* ─── CONFIRM MODAL ─── */}
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px) scale(0.98) } to { opacity: 1; transform: none } }
        @keyframes cfmFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cfmFadeOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes cfmBoxIn { from { opacity: 0; transform: scale(0.92) translateY(10px) } to { opacity: 1; transform: none } }
        @keyframes cfmBoxOut { from { opacity: 1; transform: none } to { opacity: 0; transform: scale(0.95) translateY(6px) } }
      `}</style>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTS TAB
   ═══════════════════════════════════════════════════════════ */
const CompTab = ({ comps, setComps, laborRates, equipmentRates, productionRates, onConfirm }) => {
  const update = (i, f, v) => setComps(comps.map((c, j) => j === i ? { ...c, [f]: v } : c))
  const remove = (i) => {
    const c = comps[i]
    onConfirm({
      title: 'Remove Component',
      subtitle: 'This action cannot be undone',
      message: `Remove "${c.description || 'this component'}" from the recipe?`,
      actionLabel: 'Remove',
      onConfirm: () => setComps(comps.filter((_, j) => j !== i))
    })
  }
  const add = () => setComps([...comps, { costType: 'labor', description: '', laborRole: '', quantityMode: 'service_total', defaultQty: 0, isOptional: false, followsLeadHours: false, minimumHours: null, minimumCost: null }])

  const sel = (v, onChange, opts, style = {}) => (
    <select value={v} onChange={onChange}
      style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 12, background: T.input, color: T.textPrimary, fontFamily: T.font, outline: 'none', ...style }}>
      {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  )

  return (
    <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, boxShadow: T.shadow, position: 'relative' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: T.card }}>
            {['', 'TYPE', 'DESCRIPTION', 'ROLE / ITEM', 'PROD RATE', 'QTY MODE', 'MINIMUMS', 'FLAGS', ''].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '11px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, borderBottom: `1px solid ${T.border}`, fontFamily: T.font }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {comps.map((c, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = T.cardHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ padding: '10px 8px 10px 14px', width: 28, color: T.textDim, cursor: 'grab', opacity: 0.3 }}>&#x2630;</td>
              <td style={{ padding: '10px 14px' }}>
                <CostTypeSelector value={c.costType} onChange={type => {
                  const updates = { costType: type }
                  if (type !== 'labor') { updates.laborRole = ''; updates.followsLeadHours = false; updates.productionRateId = '' }
                  if (type !== 'equipment') updates.equipmentRateId = ''
                  setComps(comps.map((cc, j) => j === i ? { ...cc, ...updates } : cc))
                }} />
              </td>
              <td style={{ padding: '10px 14px' }}>
                <input value={c.description || ''} onChange={e => update(i, 'description', e.target.value)} placeholder="Component name"
                  style={{ border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: T.textBright, width: '100%', outline: 'none', fontFamily: T.font }} />
              </td>
              <td style={{ padding: '10px 14px', fontSize: 12, color: T.textSecondary }}>
                {c.costType === 'labor' ? (
                  <Autocomplete
                    size="small" freeSolo
                    options={laborRates.map(lr => lr.role)}
                    value={c.laborRole || ''}
                    onInputChange={(_, val) => update(i, 'laborRole', val)}
                    renderOption={opt => { const lr = laborRates.find(r => r.role === opt); return <span>{opt}{lr ? <span style={{ color: T.textDim, marginLeft: 6, fontSize: 11 }}>${lr.hourlyRate}/hr</span> : null}</span> }}
                    renderInput={params => <MuiTextField {...params} variant="outlined" placeholder="Role..." InputProps={{ ...params.InputProps, style: { fontSize: 12, padding: '2px 4px' } }} />}
                    style={{ minWidth: 160 }}
                  />
                ) : c.costType === 'equipment' ? (
                  <Autocomplete
                    size="small"
                    options={equipmentRates}
                    getOptionLabel={er => `${er.name} ($${er.hourlyRate}/hr)`}
                    value={equipmentRates.find(er => er._id === c.equipmentRateId) || null}
                    onChange={(_, er) => update(i, 'equipmentRateId', er?._id || '')}
                    renderInput={params => <MuiTextField {...params} variant="outlined" placeholder="Equipment..." InputProps={{ ...params.InputProps, style: { fontSize: 12, padding: '2px 4px' } }} />}
                    style={{ minWidth: 160 }}
                  />
                ) : <span style={{ color: T.textDim }}>{'\u2014'}</span>}
              </td>
              <td style={{ padding: '10px 14px', fontSize: 12 }}>
                {c.costType === 'labor' ? (
                  <Autocomplete
                    size="small"
                    options={productionRates}
                    getOptionLabel={pr => `${pr.description} (${pr.baseRate} ${pr.unit})`}
                    value={productionRates.find(pr => pr._id === c.productionRateId) || null}
                    onChange={(_, pr) => update(i, 'productionRateId', pr?._id || '')}
                    renderInput={params => <MuiTextField {...params} variant="outlined" placeholder="Search rates..." InputProps={{ ...params.InputProps, style: { fontSize: 11, fontFamily: T.mono, padding: '2px 4px' } }} />}
                    style={{ minWidth: 200 }}
                    noOptionsText="No matching rates"
                  />
                ) : <span style={{ fontFamily: T.mono, color: T.textDim, fontSize: 11 }}>{c.quantityMode === 'manual' ? `Qty: ${c.defaultQty || 0}` : 'Svc Total'}</span>}
              </td>
              <td style={{ padding: '10px 14px' }}>
                {sel(c.quantityMode || 'service_total', e => update(i, 'quantityMode', e.target.value),
                  [['service_total', 'Svc Total'], ['manual', 'Manual']], { fontFamily: T.mono, fontSize: 11 })}
                {c.quantityMode === 'manual' && (
                  <input type="number" value={c.defaultQty || ''} onChange={e => update(i, 'defaultQty', parseFloat(e.target.value) || 0)}
                    style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 6px', fontSize: 11, background: T.input, color: T.textPrimary, fontFamily: T.mono, width: 50, marginLeft: 4, outline: 'none' }} />
                )}
              </td>
              <td style={{ padding: '10px 14px' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontSize: 10, color: T.textDim, fontFamily: T.mono }}>hrs</span>
                    <input type="number" value={c.minimumHours || ''} placeholder={'\u2014'}
                      onChange={e => update(i, 'minimumHours', e.target.value ? parseFloat(e.target.value) : null)}
                      style={{
                        width: 52, padding: '3px 6px', border: `1px solid ${T.border}`, borderRadius: 4,
                        fontSize: 11, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none',
                        textAlign: 'center', transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = T.accent}
                      onBlur={e => e.target.style.borderColor = T.border} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontSize: 10, color: T.textDim, fontFamily: T.mono }}>$</span>
                    <input type="number" value={c.minimumCost || ''} placeholder={'\u2014'}
                      onChange={e => update(i, 'minimumCost', e.target.value ? parseFloat(e.target.value) : null)}
                      style={{
                        width: 52, padding: '3px 6px', border: `1px solid ${T.border}`, borderRadius: 4,
                        fontSize: 11, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none',
                        textAlign: 'center', transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = T.accent}
                      onBlur={e => e.target.style.borderColor = T.border} />
                  </div>
                </div>
              </td>
              <td style={{ padding: '10px 14px' }}>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FlagToggle label="Optional" icon={c.isOptional ? '\u25C9' : '\u25CB'} color={T.warn}
                    active={c.isOptional} onClick={() => update(i, 'isOptional', !c.isOptional)} />
                  <FlagToggle label="Follows" icon={'\u21E3'} color="#6366f1" laborOnly costType={c.costType}
                    active={c.followsLeadHours} onClick={() => update(i, 'followsLeadHours', !c.followsLeadHours)} />
                </div>
              </td>
              <td style={{ padding: '10px 8px', width: 36 }}>
                <button onClick={() => remove(i)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 16, padding: 4, borderRadius: 4, transition: 'all 0.15s', lineHeight: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.color = T.danger; e.currentTarget.style.background = T.dangerDim }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.textDim; e.currentTarget.style.background = 'none' }}>&times;</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div onClick={add}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14, borderTop: `1px dashed ${T.border}`, cursor: 'pointer', color: T.textDim, fontSize: 13, fontWeight: 500, transition: 'all 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.background = T.cardHover; e.currentTarget.style.color = T.accent }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textDim }}>
        + Add Component
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   INPUT SLOTS TAB
   ═══════════════════════════════════════════════════════════ */
const FALLBACK_UNITS = ['LF', 'SF', 'SY', 'CY', 'EA', 'GAL', 'TON', 'HR', 'LS']

const InputSlotsTab = ({ slots, setSlots, onConfirm, productionRates }) => {
  const units = useMemo(() => {
    const fromRates = new Set((productionRates || []).map(pr => pr.unit).filter(Boolean))
    FALLBACK_UNITS.forEach(u => fromRates.add(u))
    return [...fromRates].sort()
  }, [productionRates])
  const update = (i, field, val) => setSlots(slots.map((s, j) => j === i ? { ...s, [field]: val } : s))
  const remove = (i) => {
    const s = slots[i]
    onConfirm({
      title: 'Remove Input Slot',
      subtitle: 'This action cannot be undone',
      message: `Remove "${s.label || s.key || 'this slot'}" from the recipe inputs?`,
      actionLabel: 'Remove',
      onConfirm: () => setSlots(slots.filter((_, j) => j !== i))
    })
  }
  const add = () => setSlots([...slots, { key: `input_${Date.now()}`, label: '', unit: 'LF', defaultValue: null, tags: [] }])

  const autoKey = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textBright }}>Input Requirements ({slots.length})</div>
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>Define what measurements and quantities this recipe needs to calculate costs</div>
        </div>
        <button onClick={add}
          style={{ padding: '7px 16px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.font, color: T.textPrimary, transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textPrimary }}>+ Add Input</button>
      </div>

      {slots.length === 0 && (
        <div style={{ padding: 56, textAlign: 'center', border: `2px dashed ${T.border}`, borderRadius: T.radiusXl, color: T.textDim }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>&#x1F4D0;</div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>No input requirements defined</div>
          <div style={{ fontSize: 12 }}>Add inputs like "Lateral Line (LF)" or "Area to Spray (SF)" to make this recipe self-describing</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {slots.map((slot, i) => (
          <div key={i} style={{
            background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
            padding: '16px 20px', boxShadow: T.shadow, transition: 'border-color 0.2s'
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.borderActive}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {/* Drag handle */}
              <div style={{ color: T.textDim, cursor: 'grab', opacity: 0.3, paddingTop: 6, fontSize: 14 }}>&#x2630;</div>

              {/* Label */}
              <div style={{ flex: 2 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Label</div>
                <input value={slot.label || ''} placeholder="e.g. Lateral Line"
                  onChange={e => {
                    const label = e.target.value
                    const newSlots = [...slots]
                    newSlots[i] = { ...newSlots[i], label }
                    if (!slot._keyEdited) newSlots[i].key = autoKey(label)
                    setSlots(newSlots)
                  }}
                  style={{
                    width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                    fontSize: 14, fontWeight: 500, color: T.textBright, background: T.panel, outline: 'none',
                    fontFamily: T.font, transition: 'border-color 0.2s', boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = T.accent}
                  onBlur={e => e.target.style.borderColor = T.border} />
              </div>

              {/* Unit */}
              <div style={{ width: 100 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Unit</div>
                <div style={{ position: 'relative' }}>
                  <input value={slot.unit || ''} placeholder="LF"
                    onChange={e => update(i, 'unit', e.target.value)}
                    list={`units-${i}`}
                    style={{
                      width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                      fontSize: 13, fontFamily: T.mono, fontWeight: 600, color: T.accent, background: T.panel, outline: 'none',
                      transition: 'border-color 0.2s', boxSizing: 'border-box'
                    }}
                    onFocus={e => e.target.style.borderColor = T.accent}
                    onBlur={e => e.target.style.borderColor = T.border} />
                  <datalist id={`units-${i}`}>
                    {units.map(u => <option key={u} value={u} />)}
                  </datalist>
                </div>
              </div>

              {/* Default Value */}
              <div style={{ width: 100 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Default</div>
                <input type="number" value={slot.defaultValue ?? ''} placeholder="\u2014"
                  onChange={e => update(i, 'defaultValue', e.target.value ? parseFloat(e.target.value) : null)}
                  style={{
                    width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                    fontSize: 13, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none',
                    transition: 'border-color 0.2s', textAlign: 'center', boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = T.accent}
                  onBlur={e => e.target.style.borderColor = T.border} />
              </div>

              {/* Remove */}
              <div style={{ paddingTop: 20 }}>
                <button onClick={() => remove(i)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 16, padding: 4, borderRadius: 4, transition: 'all 0.15s', lineHeight: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.color = T.danger; e.currentTarget.style.background = T.dangerDim }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.textDim; e.currentTarget.style.background = 'none' }}>&times;</button>
              </div>
            </div>

            {/* Tags row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingLeft: 26 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim }}>Key:</span>
              <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textDim, background: T.input, padding: '2px 8px', borderRadius: 4 }}>{slot.key || '\u2014'}</span>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginLeft: 12 }}>Tags:</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1, alignItems: 'center' }}>
                {(slot.tags || []).map((tag, ti) => (
                  <span key={ti} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontFamily: T.mono,
                    padding: '2px 8px', borderRadius: 4, background: `${T.accent}10`, color: T.accent, fontWeight: 500
                  }}>
                    {tag}
                    <span onClick={() => {
                      const newTags = [...(slot.tags || [])]; newTags.splice(ti, 1)
                      update(i, 'tags', newTags)
                    }} style={{ cursor: 'pointer', fontSize: 9, marginLeft: 2, opacity: 0.6 }}>x</span>
                  </span>
                ))}
                <input placeholder="+ tag" style={{
                  border: 'none', background: 'transparent', fontSize: 11, fontFamily: T.mono,
                  color: T.textDim, outline: 'none', width: 60
                }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      update(i, 'tags', [...(slot.tags || []), e.target.value.trim().toLowerCase()])
                      e.target.value = ''
                    }
                  }} />
              </div>
            </div>

            {/* Size classes toggle + editor */}
            <div style={{ marginTop: 12, paddingLeft: 26 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: slot.isSizeClassInput ? T.accent : T.textDim }}>
                <input type="checkbox" checked={!!slot.isSizeClassInput}
                  onChange={e => {
                    const newSlots = [...slots]
                    newSlots[i] = { ...newSlots[i], isSizeClassInput: e.target.checked, quantityClasses: newSlots[i].quantityClasses || [] }
                    setSlots(newSlots)
                  }} />
                Has Size Classes
                <span style={{ fontSize: 10, color: T.textDim, fontWeight: 400, fontStyle: 'italic' }}>(e.g. 15gal, 24" box, 36" box)</span>
              </label>

              {slot.isSizeClassInput && (
                <div style={{ marginTop: 10, padding: 12, background: T.card, borderRadius: T.radius, border: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim }}>Size Classes</span>
                    <button onClick={() => {
                      const newSlots = [...slots]
                      const classes = [...(newSlots[i].quantityClasses || []), { classKey: `class_${Date.now()}`, label: '', productionRateId: null, productionRateHint: '', sortOrder: (newSlots[i].quantityClasses || []).length, equipmentRules: [] }]
                      newSlots[i] = { ...newSlots[i], quantityClasses: classes }
                      setSlots(newSlots)
                    }}
                      style={{ padding: '2px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: T.textDim, fontFamily: T.mono }}>+ class</button>
                  </div>
                  {(slot.quantityClasses || []).map((cls, ci) => (
                    <div key={ci} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <input value={cls.classKey || ''} placeholder="key"
                        onChange={e => {
                          const newSlots = [...slots]
                          const classes = [...(newSlots[i].quantityClasses || [])]
                          classes[ci] = { ...classes[ci], classKey: e.target.value }
                          newSlots[i] = { ...newSlots[i], quantityClasses: classes }
                          setSlots(newSlots)
                        }}
                        style={{ width: 80, padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 11, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none' }} />
                      <input value={cls.label || ''} placeholder="Label (e.g. 24&quot; Box)"
                        onChange={e => {
                          const newSlots = [...slots]
                          const classes = [...(newSlots[i].quantityClasses || [])]
                          classes[ci] = { ...classes[ci], label: e.target.value }
                          newSlots[i] = { ...newSlots[i], quantityClasses: classes }
                          setSlots(newSlots)
                        }}
                        style={{ flex: 1, padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 11, color: T.textPrimary, background: T.panel, outline: 'none' }} />
                      <Autocomplete size="small" options={productionRates || []}
                        getOptionLabel={o => o ? `${o.description || ''} (${o.baseRate} ${o.unit}/hr)` : ''}
                        value={(productionRates || []).find(p => p._id === cls.productionRateId) || null}
                        onChange={(_, val) => {
                          const newSlots = [...slots]
                          const classes = [...(newSlots[i].quantityClasses || [])]
                          classes[ci] = { ...classes[ci], productionRateId: val ? val._id : null, productionRateHint: val ? val.description : '' }
                          newSlots[i] = { ...newSlots[i], quantityClasses: classes }
                          setSlots(newSlots)
                        }}
                        renderInput={params => <MuiTextField {...params} placeholder="Production rate..." variant="outlined" size="small"
                          style={{ minWidth: 220 }} InputProps={{ ...params.InputProps, style: { fontSize: 11, fontFamily: T.mono, padding: '2px 6px' } }} />}
                      />
                      <button onClick={() => {
                        const newSlots = [...slots]
                        const classes = [...(newSlots[i].quantityClasses || [])]
                        classes.splice(ci, 1)
                        newSlots[i] = { ...newSlots[i], quantityClasses: classes }
                        setSlots(newSlots)
                      }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 12, padding: 2 }}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   MATERIALS TAB
   ═══════════════════════════════════════════════════════════ */
const MaterialsTab = ({ materials, setMaterials, inputSlots, onConfirm }) => {
  const update = (i, field, val) => setMaterials(materials.map((m, j) => j === i ? { ...m, [field]: val } : m))
  const remove = (i) => {
    const m = materials[i]
    onConfirm({
      title: 'Remove Material Rule',
      subtitle: 'This action cannot be undone',
      message: `Remove "${m.label || 'this material'}" from the recipe?`,
      actionLabel: 'Remove',
      onConfirm: () => setMaterials(materials.filter((_, j) => j !== i))
    })
  }
  const add = () => setMaterials([...materials, { label: '', inputSlotKey: '', qtyPerUnit: 1, formula: null, unit: '', perClass: false, classFilter: [], tags: [], isRequired: true }])

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textBright }}>Material Rules ({materials.length})</div>
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>Define materials that are auto-calculated from input quantities or formulas</div>
        </div>
        <button onClick={add}
          style={{ padding: '7px 16px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.font, color: T.textPrimary, transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textPrimary }}>+ Add Material</button>
      </div>

      {materials.length === 0 && (
        <div style={{ padding: 56, textAlign: 'center', border: `2px dashed ${T.border}`, borderRadius: T.radiusXl, color: T.textDim }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>{'\uD83E\uDDF1'}</div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>No material rules defined</div>
          <div style={{ fontSize: 12 }}>Add rules like "4 emitters per tree" or formula-based calculations</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {materials.map((mat, i) => {
          const isFormula = !!mat.formula
          return (
            <div key={i} style={{
              background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
              padding: '16px 20px', boxShadow: T.shadow, transition: 'border-color 0.2s'
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.borderActive}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ color: T.textDim, cursor: 'grab', opacity: 0.3, paddingTop: 6, fontSize: 14 }}>{'\u2630'}</div>

                {/* Label */}
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Material Label</div>
                  <input value={mat.label || ''} placeholder="e.g. Drip Emitters"
                    onChange={e => update(i, 'label', e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius, fontSize: 14, fontWeight: 500, color: T.textBright, background: T.panel, outline: 'none', fontFamily: T.font, transition: 'border-color 0.2s', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
                </div>

                {/* Unit */}
                <div style={{ width: 80 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Unit</div>
                  <input value={mat.unit || ''} placeholder="EA"
                    onChange={e => update(i, 'unit', e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontFamily: T.mono, fontWeight: 600, color: T.accent, background: T.panel, outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
                </div>

                {/* Remove */}
                <div style={{ paddingTop: 20 }}>
                  <button onClick={() => remove(i)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 16, padding: 4, borderRadius: 4, transition: 'all 0.15s', lineHeight: 1 }}
                    onMouseEnter={e => { e.currentTarget.style.color = T.danger; e.currentTarget.style.background = T.dangerDim }}
                    onMouseLeave={e => { e.currentTarget.style.color = T.textDim; e.currentTarget.style.background = 'none' }}>&times;</button>
                </div>
              </div>

              {/* Mode toggle + config */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12, paddingLeft: 26 }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => update(i, 'formula', null)}
                    style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, fontFamily: T.mono, cursor: 'pointer', border: !isFormula ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`, background: !isFormula ? T.accentDim : 'transparent', color: !isFormula ? T.accent : T.textDim, transition: 'all 0.15s' }}>
                    Per Unit
                  </button>
                  <button onClick={() => update(i, 'formula', mat.formula || '')}
                    style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, fontFamily: T.mono, cursor: 'pointer', border: isFormula ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`, background: isFormula ? T.accentDim : 'transparent', color: isFormula ? T.accent : T.textDim, transition: 'all 0.15s' }}>
                    Formula
                  </button>
                </div>

                {!isFormula ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: T.textDim, fontWeight: 600 }}>Input:</span>
                      <select value={mat.inputSlotKey || ''} onChange={e => update(i, 'inputSlotKey', e.target.value)}
                        style={{ padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none' }}>
                        <option value="">-- select --</option>
                        {(inputSlots || []).map(s => <option key={s.key} value={s.key}>{s.label || s.key} ({s.unit})</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: T.textDim, fontWeight: 600 }}>Qty/unit:</span>
                      <input type="number" value={mat.qtyPerUnit ?? 1} step="0.1"
                        onChange={e => update(i, 'qtyPerUnit', parseFloat(e.target.value) || 1)}
                        style={{ width: 70, padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12, fontFamily: T.mono, color: T.accent, textAlign: 'center', outline: 'none', background: T.panel }} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.textDim, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!mat.perClass} onChange={e => update(i, 'perClass', e.target.checked)} />
                      Per class
                    </label>
                  </>
                ) : (
                  <div style={{ flex: 1 }}>
                    <input value={mat.formula || ''} placeholder="e.g. area_sf * (depth_in / 12) / 27"
                      onChange={e => update(i, 'formula', e.target.value)}
                      style={{ width: '100%', padding: '6px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius, fontSize: 12, fontFamily: T.mono, color: T.textBright, background: T.input, outline: 'none', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
                    <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>
                      Available: {(inputSlots || []).map(s => s.key).join(', ') || 'no inputs defined'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   CHILD RECIPES TAB
   ═══════════════════════════════════════════════════════════ */
const ChildRecipesTab = ({ childRecipes, setChildRecipes, services, inputSlots, onConfirm }) => {
  const update = (i, field, val) => setChildRecipes(childRecipes.map((cr, j) => j === i ? { ...cr, [field]: val } : cr))
  const remove = (i) => {
    const cr = childRecipes[i]
    onConfirm({
      title: 'Remove Child Recipe',
      subtitle: 'This action cannot be undone',
      message: `Remove "${cr.label || cr.childServiceId || 'this child recipe'}"?`,
      actionLabel: 'Remove',
      onConfirm: () => setChildRecipes(childRecipes.filter((_, j) => j !== i))
    })
  }
  const add = () => setChildRecipes([...childRecipes, { childServiceId: '', label: '', inputMapping: [], inclusionMode: 'always', sortOrder: childRecipes.length }])

  const updateMapping = (crIdx, mapIdx, field, val) => {
    const cr = { ...childRecipes[crIdx] }
    cr.inputMapping = (cr.inputMapping || []).map((m, j) => j === mapIdx ? { ...m, [field]: val } : m)
    setChildRecipes(childRecipes.map((c, j) => j === crIdx ? cr : c))
  }
  const addMapping = (crIdx) => {
    const cr = { ...childRecipes[crIdx] }
    cr.inputMapping = [...(cr.inputMapping || []), { parentInputKey: '', childInputKey: '', multiplier: 1 }]
    setChildRecipes(childRecipes.map((c, j) => j === crIdx ? cr : c))
  }
  const removeMapping = (crIdx, mapIdx) => {
    const cr = { ...childRecipes[crIdx] }
    cr.inputMapping = (cr.inputMapping || []).filter((_, j) => j !== mapIdx)
    setChildRecipes(childRecipes.map((c, j) => j === crIdx ? cr : c))
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textBright }}>Child Recipes ({childRecipes.length})</div>
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>Compose this service from sub-recipes with input mappings</div>
        </div>
        <button onClick={add}
          style={{ padding: '7px 16px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.font, color: T.textPrimary, transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textPrimary }}>+ Add Child Recipe</button>
      </div>

      {childRecipes.length === 0 && (
        <div style={{ padding: 56, textAlign: 'center', border: `2px dashed ${T.border}`, borderRadius: T.radiusXl, color: T.textDim }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>{'\uD83E\uDDE9'}</div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>No child recipes defined</div>
          <div style={{ fontSize: 12 }}>Add sub-services like "Irrigation Emitter Tie-ins" that compose into this recipe</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {childRecipes.map((cr, i) => (
          <div key={i} style={{
            background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
            padding: '16px 20px', boxShadow: T.shadow
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 2 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Child Service</div>
                <select value={cr.childServiceId || ''} onChange={e => update(i, 'childServiceId', e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, color: T.textBright, background: T.panel, outline: 'none', fontFamily: T.font }}>
                  <option value="">-- select service --</option>
                  {(services || []).map(s => <option key={s.serviceId} value={s.serviceId}>{s.name} ({s.serviceId})</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Label Override</div>
                <input value={cr.label || ''} placeholder="Optional"
                  onChange={e => update(i, 'label', e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, color: T.textBright, background: T.panel, outline: 'none', fontFamily: T.font, boxSizing: 'border-box' }} />
              </div>
              <div style={{ width: 120 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 4 }}>Include</div>
                <select value={cr.inclusionMode || 'always'} onChange={e => update(i, 'inclusionMode', e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${T.border}`, borderRadius: T.radius, fontSize: 12, color: T.textPrimary, background: T.panel, outline: 'none' }}>
                  <option value="always">Always</option>
                  <option value="variation">By Variation</option>
                </select>
              </div>
              <div style={{ paddingTop: 20 }}>
                <button onClick={() => remove(i)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 16, padding: 4, borderRadius: 4, transition: 'all 0.15s', lineHeight: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.color = T.danger; e.currentTarget.style.background = T.dangerDim }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.textDim; e.currentTarget.style.background = 'none' }}>&times;</button>
              </div>
            </div>

            {/* Input Mappings */}
            <div style={{ marginTop: 12, paddingLeft: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim }}>Input Mappings</span>
                <button onClick={() => addMapping(i)}
                  style={{ padding: '2px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: T.textDim, fontFamily: T.mono }}>+ map</button>
              </div>
              {(cr.inputMapping || []).map((m, mi) => (
                <div key={mi} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <select value={m.parentInputKey || ''} onChange={e => updateMapping(i, mi, 'parentInputKey', e.target.value)}
                    style={{ flex: 1, padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 11, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none' }}>
                    <option value="">parent input...</option>
                    {(inputSlots || []).map(s => <option key={s.key} value={s.key}>{s.label || s.key}</option>)}
                  </select>
                  <span style={{ fontSize: 11, color: T.textDim }}>{'\u2192'}</span>
                  <input value={m.childInputKey || ''} placeholder="child_key"
                    onChange={e => updateMapping(i, mi, 'childInputKey', e.target.value)}
                    style={{ flex: 1, padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 11, fontFamily: T.mono, color: T.textPrimary, background: T.panel, outline: 'none' }} />
                  <span style={{ fontSize: 11, color: T.textDim }}>{'\u00D7'}</span>
                  <input type="number" value={m.multiplier ?? 1} step="0.1"
                    onChange={e => updateMapping(i, mi, 'multiplier', parseFloat(e.target.value) || 1)}
                    style={{ width: 60, padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 11, fontFamily: T.mono, color: T.accent, textAlign: 'center', outline: 'none', background: T.panel }} />
                  <button onClick={() => removeMapping(i, mi)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 12, padding: 2 }}>&times;</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   VARIATIONS TAB
   ═══════════════════════════════════════════════════════════ */
const VarTab = ({ vars, setVars, onConfirm, onOpenEffectBuilder, onRemoveEffect }) => {
  const up = (i, u) => setVars(vars.map((v, j) => j === i ? { ...v, ...u } : v))
  const rm = (i) => {
    const v = vars[i]
    onConfirm({
      title: 'Delete Variation',
      subtitle: 'This action cannot be undone',
      message: `Delete "${v.label || 'this variation'}" and all its rules/effects?`,
      actionLabel: 'Delete',
      onConfirm: () => setVars(vars.filter((_, j) => j !== i))
    })
  }
  const add = () => setVars([...vars, { attributeId: `var_${Date.now()}`, label: '', type: 'select', defaultValue: 'opt_1', options: [{ value: 'opt_1', label: 'Option 1' }, { value: 'opt_2', label: 'Option 2' }], rules: [] }])

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textBright }}>Variation Definitions ({vars.length})</div>
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>Control how recipes adapt to site conditions</div>
        </div>
        <button onClick={add}
          style={{ padding: '7px 16px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.font, color: T.textPrimary, transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textPrimary }}>+ Add Variation</button>
      </div>

      {vars.length === 0 && (
        <div style={{ padding: 56, textAlign: 'center', border: `2px dashed ${T.border}`, borderRadius: T.radiusXl, color: T.textDim }}>
          <div style={{ fontSize: 14, marginBottom: 4 }}>No variations defined</div>
          <div style={{ fontSize: 12 }}>Add a variation to let estimators adjust the recipe for site conditions</div>
        </div>
      )}

      {vars.map((v, vi) => (
        <div key={vi} style={{
          background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusXl,
          marginBottom: 20, boxShadow: T.shadow, overflow: 'hidden', transition: 'border-color 0.2s'
        }}
          onMouseEnter={e => e.currentTarget.style.borderColor = T.borderActive}
          onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
          {/* Card header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
            borderBottom: `1px solid ${T.border}`, background: T.card
          }}>
            <input value={v.label || ''} onChange={e => up(vi, { label: e.target.value })} placeholder="Variation name (e.g. Soil Condition)"
              style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 16, fontWeight: 600, color: T.textBright, outline: 'none', fontFamily: T.font }} />
            <span style={{ fontSize: 9, fontFamily: T.mono, fontWeight: 700, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 8px', border: `1px solid ${T.border}`, borderRadius: 4, background: T.panel }}>{v.type}</span>
            <button onClick={() => rm(vi)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 16, padding: 4, borderRadius: 4, transition: 'all 0.15s', lineHeight: 1 }}
              onMouseEnter={e => { e.currentTarget.style.color = T.danger; e.currentTarget.style.background = T.dangerDim }}
              onMouseLeave={e => { e.currentTarget.style.color = T.textDim; e.currentTarget.style.background = 'none' }}>&times;</button>
          </div>

          {/* Card body */}
          <div style={{ padding: '16px 20px 20px' }}>
            {/* Meta row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <select value={v.type} onChange={e => {
                const t = e.target.value; const u = { type: t }
                if (t === 'boolean') { u.options = []; u.defaultValue = false }
                if (t === 'select' && (!v.options || !v.options.length)) { u.options = [{ value: 'opt_1', label: 'Option 1' }]; u.defaultValue = 'opt_1' }
                if (t === 'integer') { u.defaultValue = 0; u.min = null; u.max = null }
                if (t === 'range') { u.min = 0; u.max = 100; u.step = 1; u.defaultValue = 0 }
                up(vi, u)
              }} style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, background: T.input, fontFamily: T.font, color: T.textPrimary, outline: 'none' }}>
                <option value="select">Select</option>
                <option value="boolean">Toggle</option>
                <option value="integer">Integer</option>
                <option value="range">Range</option>
              </select>
              <input value={v.attributeId || ''} onChange={e => up(vi, { attributeId: e.target.value })} placeholder="attribute_id"
                style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: T.mono, color: T.textDim, background: T.input, outline: 'none' }} />
            </div>

            {/* Select type options */}
            {v.type === 'select' && (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(v.options || []).map((opt, oi) => {
                    const isSel = opt.value === v.defaultValue
                    const effs = (v.rules || []).filter(r => evalCond(r.when, opt.value)).flatMap(r => r.effects || [])
                    return (
                      <div key={oi} onClick={() => up(vi, { defaultValue: opt.value })}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                          border: isSel ? `1.5px solid ${T.accent}` : `1.5px solid ${T.border}`,
                          background: isSel ? T.accentDim : T.panel, borderRadius: T.radiusLg,
                          cursor: 'pointer', transition: 'all 0.2s', position: 'relative'
                        }}>
                        {/* Radio */}
                        <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isSel ? T.accent : T.borderActive}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'border-color 0.15s' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent, transform: isSel ? 'scale(1)' : 'scale(0)', transition: 'transform 0.15s' }} />
                        </div>
                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <input value={opt.label || ''} onChange={e => { e.stopPropagation(); const opts = [...(v.options || [])]; opts[oi] = { ...opts[oi], label: e.target.value }; up(vi, { options: opts }) }}
                            onClick={e => e.stopPropagation()} placeholder="Option label"
                            style={{ border: 'none', background: 'transparent', fontSize: 14, fontWeight: 500, color: T.textBright, width: '100%', outline: 'none', fontFamily: T.font }} />
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: effs.length || isSel ? 6 : 0, alignItems: 'center' }}>
                            {effs.map((e, ei) => {
                              const removeEff = () => onRemoveEffect(vi, opt.value, ei)
                              if (e.action === 'exclude_component') return <Tag key={ei} type="exclude" onRemove={removeEff}>{'\uD83D\uDEAB'} Exclude: {e.targetDescription || e.targetId}</Tag>
                              if (e.action === 'include_component') return <Tag key={ei} type="include" onRemove={removeEff}>{'\u2705'} Include: {e.targetDescription || e.targetId}</Tag>
                              if (e.action === 'multiply_qty') return <Tag key={ei} type="modify" onRemove={removeEff}>{'\u2716\uFE0F'} {e.targetDescription || e.targetId} x{e.value}</Tag>
                              if (e.action === 'add_component') return <Tag key={ei} type="add" onRemove={removeEff}>{'\u2795'} {e.value?.description}</Tag>
                              return null
                            })}
                            {isSel && effs.length === 0 && <span style={{ fontSize: 10, color: T.textDim, fontStyle: 'italic' }}>Default (no changes)</span>}
                            <button onClick={e => { e.stopPropagation(); onOpenEffectBuilder(vi, opt.value) }}
                              style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.accent, fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4, transition: 'all 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.background = T.accentDim}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}>+ effect</button>
                          </div>
                        </div>
                        {/* Remove option */}
                        <button onClick={e => {
                          e.stopPropagation()
                          onConfirm({
                            title: 'Remove Option',
                            subtitle: 'This action cannot be undone',
                            message: `Remove "${opt.label || opt.value}" and any effects attached to it?`,
                            actionLabel: 'Remove',
                            onConfirm: () => { const opts = (v.options || []).filter((_, j) => j !== oi); up(vi, { options: opts }) }
                          })
                        }}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 16, padding: 2, opacity: 0.4, transition: 'all 0.15s', lineHeight: 1 }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = T.danger }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = 0.4; e.currentTarget.style.color = T.textDim }}>&times;</button>
                      </div>
                    )
                  })}
                </div>
                <button onClick={() => { const nv = `opt_${(v.options || []).length + 1}`; up(vi, { options: [...(v.options || []), { value: nv, label: '' }] }) }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, fontSize: 12, fontWeight: 500, padding: '8px 0', fontFamily: T.font, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = T.accent} onMouseLeave={e => e.currentTarget.style.color = T.textDim}>+ Add Option</button>
              </div>
            )}

            {/* Boolean type */}
            {v.type === 'boolean' && (() => {
              const effsTrue = (v.rules || []).filter(r => evalCond(r.when, true)).flatMap(r => r.effects || [])
              return (
                <div style={{ padding: '12px 14px', background: T.card, borderRadius: T.radiusLg, border: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: effsTrue.length ? 10 : 0 }}>
                    <span style={{ fontSize: 13, color: T.textSecondary }}>{v.defaultValue ? 'Enabled by default' : 'Disabled by default'}</span>
                    <Switch size="small" checked={!!v.defaultValue} onChange={e => up(vi, { defaultValue: e.target.checked })} color="primary" />
                  </div>
                  {effsTrue.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.textDim, marginBottom: 6 }}>When enabled:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                        {effsTrue.map((e, ei) => {
                          const removeEff = () => onRemoveEffect(vi, true, ei)
                          if (e.action === 'exclude_component') return <Tag key={ei} type="exclude" onRemove={removeEff}>{'\uD83D\uDEAB'} Exclude: {e.targetDescription}</Tag>
                          if (e.action === 'include_component') return <Tag key={ei} type="include" onRemove={removeEff}>{'\u2705'} Include: {e.targetDescription}</Tag>
                          if (e.action === 'multiply_qty') return <Tag key={ei} type="modify" onRemove={removeEff}>{'\u2716\uFE0F'} {e.targetDescription} x{e.value}</Tag>
                          if (e.action === 'add_component') return <Tag key={ei} type="add" onRemove={removeEff}>{'\u2795'} {e.value?.description}</Tag>
                          return null
                        })}
                        <button onClick={() => onOpenEffectBuilder(vi, true)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.accent, fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4, transition: 'all 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = T.accentDim}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>+ effect</button>
                      </div>
                    </div>
                  )}
                  {effsTrue.length === 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 10, color: T.textDim, fontStyle: 'italic' }}>No effects defined</span>
                      <button onClick={() => onOpenEffectBuilder(vi, true)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.accent, fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4, transition: 'all 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = T.accentDim}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>+ effect</button>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Integer type */}
            {v.type === 'integer' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontWeight: 600 }}>Default</div>
                  <input type="number" value={v.defaultValue ?? 0} onChange={e => up(vi, { defaultValue: Number(e.target.value) })}
                    style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: T.mono, background: T.input, width: 80, outline: 'none' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontWeight: 600 }}>Min</div>
                  <input type="number" value={v.min ?? ''} onChange={e => up(vi, { min: e.target.value ? Number(e.target.value) : null })} placeholder={'\u2014'}
                    style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: T.mono, background: T.input, width: 70, outline: 'none' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontWeight: 600 }}>Max</div>
                  <input type="number" value={v.max ?? ''} onChange={e => up(vi, { max: e.target.value ? Number(e.target.value) : null })} placeholder={'\u2014'}
                    style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: T.mono, background: T.input, width: 70, outline: 'none' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontWeight: 600 }}>Unit</div>
                  <input value={v.unit || ''} onChange={e => up(vi, { unit: e.target.value })} placeholder='e.g. "'
                    style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, background: T.input, width: 60, outline: 'none' }} />
                </div>
              </div>
            )}

            {/* Range type */}
            {v.type === 'range' && (
              <div style={{ display: 'flex', gap: 8 }}>
                {[['Min', 'min', 0], ['Max', 'max', 100], ['Step', 'step', 1]].map(([l, k, d]) => (
                  <div key={k}>
                    <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontWeight: 600 }}>{l}</div>
                    <input type="number" value={v[k] ?? d} onChange={e => up(vi, { [k]: Number(e.target.value) })}
                      style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: T.mono, background: T.input, width: 70, outline: 'none' }} />
                  </div>
                ))}
                <div>
                  <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontWeight: 600 }}>Unit</div>
                  <input value={v.unit || ''} onChange={e => up(vi, { unit: e.target.value })} placeholder='e.g. "'
                    style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, background: T.input, width: 60, outline: 'none' }} />
                </div>
              </div>
            )}

            {/* Rules summary */}
            {(v.rules || []).length > 0 && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: T.card, borderRadius: T.radius, border: `1px solid ${T.border}`, fontSize: 11 }}>
                <strong style={{ fontWeight: 700, color: T.textSecondary }}>{v.rules.length} rule{v.rules.length !== 1 ? 's' : ''}</strong>
                <span style={{ color: T.textDim, marginLeft: 6 }}>{v.rules.reduce((s, r) => s + (r.effects?.length || 0), 0)} effects</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   LIVE PREVIEW TAB — with actual cost calculations
   ═══════════════════════════════════════════════════════════ */

function calcPreview(components, inputSlots, slotValues, laborRates, equipmentRates, productionRates, minimumTotal, classBreakdowns, defaultMaterials) {
  // Build a map of slot values with their units for smart matching
  const slotsByUnit = {}
  for (const slot of (inputSlots || [])) {
    const val = slotValues[slot.key] ?? slot.defaultValue ?? 0
    if (!slotsByUnit[slot.unit]) slotsByUnit[slot.unit] = { value: val, slot }
    else if (val > 0 && !slotsByUnit[slot.unit].value) slotsByUnit[slot.unit] = { value: val, slot }
  }
  // Primary qty = first input slot with a value
  const primarySlot = (inputSlots || []).find(s => (slotValues[s.key] ?? s.defaultValue ?? 0) > 0) || (inputSlots || [])[0]
  const primaryQty = primarySlot ? (slotValues[primarySlot.key] ?? primarySlot.defaultValue ?? 0) : 0
  const primaryUnit = primarySlot ? primarySlot.unit : null

  // Helper: find best production rate for a labor component
  const findProdRate = (comp) => {
    // 1. If component has a linked productionRateId, use it
    if (comp.productionRateId) {
      const linked = productionRates.find(p => p._id === comp.productionRateId)
      if (linked) return linked
    }
    // 2. Match by role + unit matching the primary input slot unit
    if (primaryUnit) {
      const byRoleAndUnit = productionRates.filter(p => p.role === comp.laborRole && p.unit === primaryUnit)
      if (byRoleAndUnit.length === 1) return byRoleAndUnit[0]
      // If multiple, try description match
      if (byRoleAndUnit.length > 1) {
        const desc = (comp.description || '').toLowerCase()
        const descMatch = byRoleAndUnit.find(p => {
          const prDesc = (p.description || '').toLowerCase()
          return prDesc.includes(desc) || desc.includes(prDesc.split('(')[0].trim().toLowerCase())
        })
        if (descMatch) return descMatch
        return byRoleAndUnit[0] // first match by role+unit
      }
    }
    // 3. Last resort: any rate with matching role (but only if we have a qty for that unit)
    const byRole = productionRates.filter(p => p.role === comp.laborRole)
    for (const pr of byRole) {
      if (slotsByUnit[pr.unit] && slotsByUnit[pr.unit].value > 0) return pr
    }
    return null
  }

  // Helper: get the qty for a production rate's unit from input slots
  const getQtyForRate = (pr) => {
    if (!pr) return primaryQty
    const match = slotsByUnit[pr.unit]
    return match ? match.value : primaryQty
  }

  const rows = []
  let totalLaborHrs = 0, totalLaborCost = 0, totalEquipHrs = 0, totalEquipCost = 0
  let totalMaterialCost = 0, totalSubCost = 0, totalDisposalCost = 0

  for (const c of components) {
    if (c._excluded) { rows.push({ ...c, hours: 0, cost: 0, rate: null, prodRate: null }); continue }

    const row = { ...c, hours: 0, cost: 0, rate: null, prodRate: null }

    if (c.costType === 'labor') {
      // Check follows-lead FIRST — these don't get their own prod rate calc
      if (c.followsLeadHours) {
        const lr = laborRates.find(l => l.role === c.laborRole)
        row.rate = lr ? lr.hourlyRate : 0
        row._followsLead = true
        rows.push(row)
        continue
      }

      const pr = findProdRate(c)
      const lr = laborRates.find(l => l.role === c.laborRole)
      row.rate = lr ? lr.hourlyRate : 0

      if (pr) {
        const qty = getQtyForRate(pr)
        if (qty > 0) {
          row.prodRate = pr
          const baseHrs = qty / pr.baseRate
          row.hours = Math.max(baseHrs * (c._mult || 1), c.minimumHours || 0)
        } else {
          row.hours = c.minimumHours || 0
        }
      } else if (c.quantityMode === 'manual' && c.defaultQty > 0) {
        row.hours = Math.max(c.defaultQty * (c._mult || 1), c.minimumHours || 0)
      } else {
        row.hours = c.minimumHours || 0
      }

      row.cost = row.hours * row.rate
      totalLaborHrs += row.hours
      totalLaborCost += row.cost

    } else if (c.costType === 'equipment') {
      const er = equipmentRates.find(e => e.name === c.equipmentRole || e.name === c.description)
      row.rate = er ? (er.hourlyRate || er.rate || 0) : 0
      // Start with minimums — follows-lead will override in second pass
      row.hours = c.minimumHours || 0
      row.cost = row.hours * row.rate
      totalEquipHrs += row.hours
      totalEquipCost += row.cost

    } else if (c.costType === 'material') {
      row.cost = (c.defaultUnitCost || 0) * (c.defaultQty || primaryQty || 0)
      totalMaterialCost += row.cost

    } else if (c.costType === 'subcontract') {
      row.cost = c.defaultUnitCost || 0
      totalSubCost += row.cost

    } else if (c.costType === 'disposal') {
      row.cost = c.defaultUnitCost || 0
      totalDisposalCost += row.cost
    }

    rows.push(row)
  }

  // Second pass: find the lead (first non-following labor component with hours)
  const lead = rows.find(r => r.costType === 'labor' && !r._followsLead && !r._excluded && r.hours > 0)
  const leadHours = lead ? lead.hours : 0

  // Fill in follows-lead labor
  for (const row of rows) {
    if (row._followsLead && !row._excluded && leadHours > 0) {
      row.hours = Math.max(leadHours, row.minimumHours || 0)
      row.cost = row.hours * (row.rate || 0)
      totalLaborHrs += row.hours
      totalLaborCost += row.cost
    }
  }

  // Fill in follows-lead equipment
  for (const row of rows) {
    if (row.costType === 'equipment' && row.followsLeadHours && !row._excluded && leadHours > 0) {
      const prevHrs = row.hours
      const prevCost = row.cost
      row.hours = Math.max(leadHours, row.minimumHours || 0)
      row.cost = row.hours * (row.rate || 0)
      totalEquipHrs += row.hours - prevHrs
      totalEquipCost += row.cost - prevCost
    }
  }

  // ── Size class resolution ──
  const classDetails = []
  let sizeClassHours = 0
  for (const slot of (inputSlots || [])) {
    if (!slot.isSizeClassInput || !(slot.quantityClasses || []).length) continue
    const bk = (classBreakdowns || {})[slot.key] || {}
    for (const cls of slot.quantityClasses) {
      const qty = Number(bk[cls.classKey]) || 0
      if (qty <= 0) continue
      const pr = cls.productionRateId ? productionRates.find(p => p._id === cls.productionRateId) : null
      const hrs = pr && pr.baseRate > 0 ? qty / pr.baseRate : 0
      classDetails.push({ classKey: cls.classKey, label: cls.label, qty, prodRate: pr, hours: hrs, slotKey: slot.key })
      sizeClassHours += hrs
    }
  }
  // If size classes resolved hours, override lead
  if (sizeClassHours > 0) {
    const leadRow = rows.find(r => r.costType === 'labor' && !r._followsLead && !r._excluded)
    if (leadRow) {
      totalLaborHrs -= leadRow.hours
      totalLaborCost -= leadRow.cost
      leadRow.hours = sizeClassHours
      leadRow.cost = sizeClassHours * (leadRow.rate || 0)
      totalLaborHrs += leadRow.hours
      totalLaborCost += leadRow.cost
    }
    // Re-apply follows-lead with updated hours
    for (const row of rows) {
      if (row._followsLead && !row._excluded) {
        totalLaborHrs -= row.hours; totalLaborCost -= row.cost
        row.hours = Math.max(sizeClassHours, row.minimumHours || 0)
        row.cost = row.hours * (row.rate || 0)
        totalLaborHrs += row.hours; totalLaborCost += row.cost
      }
      if (row.costType === 'equipment' && row.followsLeadHours && !row._excluded) {
        totalEquipHrs -= row.hours; totalEquipCost -= row.cost
        row.hours = Math.max(sizeClassHours, row.minimumHours || 0)
        row.cost = row.hours * (row.rate || 0)
        totalEquipHrs += row.hours; totalEquipCost += row.cost
      }
    }
  }

  // ── Material rule resolution ──
  const materialLines = []
  for (const mat of (defaultMaterials || [])) {
    let computedQty = 0
    if (mat.formula) {
      computedQty = clientEvalFormula(mat.formula, slotValues || {})
    } else if (mat.perClass) {
      for (const slot of (inputSlots || [])) {
        if (!slot.isSizeClassInput) continue
        const bk = (classBreakdowns || {})[slot.key] || {}
        for (const cls of (slot.quantityClasses || [])) {
          if (mat.classFilter && mat.classFilter.length > 0 && !mat.classFilter.includes(cls.classKey)) continue
          computedQty += (Number(bk[cls.classKey]) || 0) * (mat.qtyPerUnit || 1)
        }
      }
    } else if (mat.inputSlotKey) {
      computedQty = ((slotValues || {})[mat.inputSlotKey] || 0) * (mat.qtyPerUnit || 1)
    }
    materialLines.push({ label: mat.label, unit: mat.unit || '', computedQty, formula: mat.formula })
  }

  const grandTotal = totalLaborCost + totalEquipCost + totalMaterialCost + totalSubCost + totalDisposalCost
  const effectiveTotal = minimumTotal ? Math.max(grandTotal, minimumTotal) : grandTotal

  return { rows, totalLaborHrs, totalLaborCost, totalEquipHrs, totalEquipCost, totalMaterialCost, totalSubCost, totalDisposalCost, grandTotal, effectiveTotal, classDetails, materialLines }
}

const fmt = (n) => n != null && n > 0 ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '\u2014'
const fmtHrs = (n) => n != null && n > 0 ? `${n.toFixed(1)} hrs` : '\u2014'

const SummaryCard = ({ label, value, color, sub }) => (
  <div style={{
    flex: 1, padding: '14px 16px', background: T.panel, borderRadius: T.radiusLg,
    border: `1px solid ${T.border}`, boxShadow: T.shadow, minWidth: 120
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: T.mono, color: color || T.textBright }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: T.textDim, fontFamily: T.mono, marginTop: 2 }}>{sub}</div>}
  </div>
)

const PreviewTab = ({ result, vars, sel, setSel, inputSlots, slotValues, setSlotValues, classBreakdowns, setClassBreakdowns, defaultMaterials, childRecipes, laborRates, equipmentRates, productionRates, minimumTotal }) => {
  const hasSizeClasses = (inputSlots || []).some(s => s.isSizeClassInput && (s.quantityClasses || []).length > 0)
  const calc = useMemo(() =>
    calcPreview(result.components, inputSlots, slotValues, laborRates || [], equipmentRates || [], productionRates || [], minimumTotal, classBreakdowns, defaultMaterials),
    [result.components, inputSlots, slotValues, laborRates, equipmentRates, productionRates, minimumTotal, classBreakdowns, defaultMaterials]
  )

  return (
    <div style={{ display: 'flex', gap: 28 }}>
      <div style={{ flex: 1 }}>
        {/* Input Slots Test Panel */}
        {inputSlots && inputSlots.length > 0 && (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: 16, marginBottom: 20, boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 12 }}>Service Quantities (Test Values)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {inputSlots.map((slot, si) => (
                <div key={si} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                  background: T.card, borderRadius: T.radius, border: `1px solid ${T.border}`, minWidth: 200
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.textBright, marginBottom: 4 }}>{slot.label || slot.key}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" value={slotValues[slot.key] ?? slot.defaultValue ?? ''} placeholder="0"
                        onChange={e => setSlotValues(prev => ({ ...prev, [slot.key]: e.target.value ? parseFloat(e.target.value) : null }))}
                        style={{
                          width: 90, padding: '6px 10px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                          fontSize: 16, fontFamily: T.mono, fontWeight: 700, color: T.accent,
                          textAlign: 'center', outline: 'none', background: T.panel, transition: 'border-color 0.2s'
                        }}
                        onFocus={e => e.target.style.borderColor = T.accent}
                        onBlur={e => e.target.style.borderColor = T.border} />
                      <span style={{ fontSize: 12, fontFamily: T.mono, fontWeight: 700, color: T.textDim }}>{slot.unit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Size Class Breakdown */}
        {hasSizeClasses && (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: 16, marginBottom: 20, boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 12 }}>Size Class Breakdown</div>
            {(inputSlots || []).filter(s => s.isSizeClassInput).map((slot, si) => (
              <div key={si}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textBright, marginBottom: 8 }}>{slot.label || slot.key}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  {(slot.quantityClasses || []).map((cls, ci) => {
                    const bk = (classBreakdowns || {})[slot.key] || {}
                    const cd = (calc.classDetails || []).find(d => d.classKey === cls.classKey && d.slotKey === slot.key)
                    return (
                      <div key={ci} style={{
                        padding: '10px 14px', background: T.card, borderRadius: T.radius,
                        border: `1px solid ${T.border}`, minWidth: 140
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textBright, marginBottom: 4 }}>{cls.label || cls.classKey}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="number" value={bk[cls.classKey] ?? ''} placeholder="0"
                            onChange={e => {
                              const val = e.target.value ? parseFloat(e.target.value) : 0
                              setClassBreakdowns(prev => ({
                                ...prev,
                                [slot.key]: { ...(prev[slot.key] || {}), [cls.classKey]: val }
                              }))
                            }}
                            style={{
                              width: 70, padding: '6px 10px', border: `1.5px solid ${T.border}`, borderRadius: T.radius,
                              fontSize: 16, fontFamily: T.mono, fontWeight: 700, color: T.accent,
                              textAlign: 'center', outline: 'none', background: T.panel
                            }}
                            onFocus={e => e.target.style.borderColor = T.accent}
                            onBlur={e => e.target.style.borderColor = T.border} />
                          <span style={{ fontSize: 12, fontFamily: T.mono, fontWeight: 700, color: T.textDim }}>{slot.unit}</span>
                        </div>
                        {cd && cd.hours > 0 && (
                          <div style={{ fontSize: 10, fontFamily: T.mono, color: T.success, marginTop: 4 }}>
                            {cd.hours.toFixed(2)} hrs{cd.prodRate ? ` @ ${cd.prodRate.baseRate} ${cd.prodRate.unit || ''}/hr` : ''}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
            {(calc.classDetails || []).length > 0 && (
              <div style={{ fontSize: 12, fontFamily: T.mono, fontWeight: 700, color: T.accent, borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
                Total class hours: {calc.classDetails.reduce((s, d) => s + d.hours, 0).toFixed(2)} hrs
              </div>
            )}
          </div>
        )}

        {/* Cost Summary Cards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <SummaryCard label="Labor Hours" value={fmtHrs(calc.totalLaborHrs)} color={CT.labor.color} />
          <SummaryCard label="Labor Cost" value={fmt(calc.totalLaborCost)} color={CT.labor.color}
            sub={calc.totalLaborHrs > 0 ? `${calc.totalLaborHrs.toFixed(1)} hrs` : null} />
          <SummaryCard label="Equipment" value={fmt(calc.totalEquipCost)} color={CT.equipment.color}
            sub={calc.totalEquipHrs > 0 ? `${calc.totalEquipHrs.toFixed(1)} hrs` : null} />
          <SummaryCard label="Materials" value={fmt(calc.totalMaterialCost)} color={CT.material.color} />
          {calc.totalSubCost > 0 && <SummaryCard label="Subcontract" value={fmt(calc.totalSubCost)} color={CT.subcontract.color} />}
          {calc.totalDisposalCost > 0 && <SummaryCard label="Disposal" value={fmt(calc.totalDisposalCost)} color={CT.disposal.color} />}
          <SummaryCard label="Total" value={fmt(calc.effectiveTotal)} color={T.textBright}
            sub={minimumTotal && calc.grandTotal < minimumTotal ? `Min applied ($${minimumTotal})` : null} />
        </div>

        {/* Active Effects */}
        {result.effects.length > 0 && (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: 16, marginBottom: 20, boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 10 }}>Active Effects ({result.effects.length})</div>
            {result.effects.map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <Tag type={e.type}>{e.type}</Tag>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textBright }}>{e.target}</span>
                <span style={{ fontSize: 11, color: T.textDim }}>{'\u2014'} {e.note || e.variation}</span>
              </div>
            ))}
          </div>
        )}

        {/* Component Breakdown Table */}
        <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, overflow: 'hidden', boxShadow: T.shadow }}>
          <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.textBright }}>Component Breakdown</span>
            <span style={{ fontSize: 11, color: T.textDim, marginLeft: 8, fontFamily: T.mono }}>{calc.rows.filter(c => !c._excluded).length} active</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: T.card }}>
                {['TYPE', 'DESCRIPTION', 'ROLE / ITEM', 'PROD RATE', 'HOURS', 'RATE', 'COST', 'STATUS'].map((h, i) => (
                  <th key={i} style={{ textAlign: i >= 4 ? 'right' : 'left', padding: '10px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, borderBottom: `1px solid ${T.border}`, fontFamily: T.font }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calc.rows.map((c, i) => {
                const isLabor = c.costType === 'labor'
                const isEquip = c.costType === 'equipment'
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, opacity: c._excluded ? 0.25 : 1, background: c._added ? `${T.success}06` : 'transparent', transition: 'all 0.15s' }}>
                    <td style={{ padding: '10px 12px' }}><Badge type={c.costType} /></td>
                    <td style={{ padding: '10px 12px', fontWeight: 500, color: T.textBright, textDecoration: c._excluded ? 'line-through' : 'none', fontSize: 13 }}>
                      {c.description}
                      {c._added && <span style={{ fontSize: 10, color: T.textDim, fontStyle: 'italic', marginLeft: 6 }}>via {c._by}</span>}
                      {c._followsLead && <span style={{ fontSize: 9, color: '#6366f1', fontStyle: 'italic', marginLeft: 4 }}>(follows lead)</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: T.textSecondary, fontSize: 12 }}>{c.laborRole || c.equipmentRole || '\u2014'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 11, fontFamily: T.mono, color: T.textDim }}>
                      {c.prodRate ? `${c.prodRate.baseRate.toLocaleString()} ${c.prodRate.unit}/hr` : c._followsLead ? 'follows lead' : (c._mult !== 1 ? `x${c._mult}` : '\u2014')}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: T.mono, fontSize: 12, fontWeight: 600, color: (isLabor || isEquip) && c.hours > 0 ? T.textBright : T.textDim }}>
                      {(isLabor || isEquip) && c.hours > 0 ? c.hours.toFixed(1) : '\u2014'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: T.mono, fontSize: 11, color: T.textDim }}>
                      {c.rate > 0 ? `$${c.rate}/hr` : '\u2014'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: c.cost > 0 ? T.success : T.textDim }}>
                      {c.cost > 0 ? `$${c.cost.toFixed(2)}` : '\u2014'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {c._excluded ? (
                        <span style={{ fontSize: 9, fontFamily: T.mono, fontWeight: 700, padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.danger, backgroundColor: T.dangerDim }}>EXCLUDED</span>
                      ) : c._added ? (
                        <span style={{ fontSize: 9, fontFamily: T.mono, fontWeight: 700, padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.success, backgroundColor: T.successDim }}>ADDED</span>
                      ) : (
                        <span style={{ fontSize: 9, fontFamily: T.mono, fontWeight: 700, padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.textDim, backgroundColor: `${T.textDim}12` }}>ACTIVE</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Material Lines */}
        {(calc.materialLines || []).length > 0 && (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, overflow: 'hidden', boxShadow: T.shadow, marginTop: 20 }}>
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.textBright }}>Computed Materials</span>
              <span style={{ fontSize: 11, color: T.textDim, marginLeft: 8, fontFamily: T.mono }}>{calc.materialLines.length} items</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.card }}>
                  {['MATERIAL', 'SOURCE', 'QTY', 'UNIT'].map((h, hi) => (
                    <th key={hi} style={{ textAlign: hi >= 2 ? 'right' : 'left', padding: '10px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, borderBottom: `1px solid ${T.border}`, fontFamily: T.font }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calc.materialLines.map((ml, mi) => (
                  <tr key={mi} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500, color: T.textBright, fontSize: 13 }}>{ml.label || 'Unnamed'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 11, fontFamily: T.mono, color: T.textDim }}>{ml.formula || 'per unit'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: ml.computedQty > 0 ? T.success : T.textDim }}>{ml.computedQty > 0 ? ml.computedQty.toFixed(2) : '\u2014'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: T.mono, fontSize: 11, fontWeight: 600, color: T.accent }}>{ml.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Child Recipes Summary */}
        {(childRecipes || []).length > 0 && (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: 16, marginTop: 20, boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 10 }}>Child Recipes ({childRecipes.length})</div>
            {childRecipes.map((cr, ci) => (
              <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: T.card, borderRadius: T.radius, border: `1px solid ${T.border}`, marginBottom: 6 }}>
                <span style={{ fontSize: 9, fontFamily: T.mono, fontWeight: 700, color: '#6366f1', background: 'rgba(99,102,241,0.08)', padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase' }}>CHILD</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textBright }}>{cr.label || cr.childServiceId}</span>
                <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textDim, marginLeft: 'auto' }}>{cr.inclusionMode}</span>
                {(cr.inputMapping || []).length > 0 && (
                  <span style={{ fontSize: 10, fontFamily: T.mono, color: T.accent }}>{cr.inputMapping.length} mapped</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right sidebar: Configuration */}
      {vars.length > 0 && (
        <div style={{ width: 260, minWidth: 260 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: T.textDim, marginBottom: 16 }}>Configuration</div>
          {vars.map((v, vi) => (
            <div key={vi} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textBright, marginBottom: 6 }}>{v.label || v.attributeId}</div>
              {v.type === 'select' && (
                <select value={sel[v.attributeId] ?? v.defaultValue} onChange={e => setSel(p => ({ ...p, [v.attributeId]: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontFamily: T.font, color: T.textPrimary, outline: 'none', cursor: 'pointer' }}>
                  {(v.options || []).map((o, oi) => <option key={oi} value={o.value}>{o.label || o.value}</option>)}
                </select>
              )}
              {v.type === 'boolean' && <Switch size="small" checked={!!sel[v.attributeId]} onChange={e => setSel(p => ({ ...p, [v.attributeId]: e.target.checked }))} color="primary" />}
              {v.type === 'integer' && (
                <input type="number" value={sel[v.attributeId] ?? v.defaultValue ?? 0}
                  min={v.min ?? undefined} max={v.max ?? undefined}
                  onChange={e => setSel(p => ({ ...p, [v.attributeId]: Number(e.target.value) }))}
                  style={{ width: '100%', padding: '8px 10px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: T.radius, fontSize: 13, fontFamily: T.mono, color: T.textPrimary, outline: 'none' }} />
              )}
              {v.type === 'range' && (
                <div>
                  <input type="range" min={v.min} max={v.max} step={v.step} value={sel[v.attributeId] ?? v.defaultValue}
                    onChange={e => setSel(p => ({ ...p, [v.attributeId]: Number(e.target.value) }))} style={{ width: '100%' }} />
                  <div style={{ fontSize: 11, fontFamily: T.mono, color: T.accent, fontWeight: 600 }}>{sel[v.attributeId] ?? v.defaultValue}{v.unit || ''}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(ServiceRecipeBuilder)
